import React, { useCallback, useEffect, useMemo, useState, useImperativeHandle, forwardRef, useRef} from "react";
import { Platform, Pressable, StyleSheet, Text, TextInput, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useTheme } from "../../../theme";
import { useSelector } from "react-redux";
import { useToast } from "../../../components/LhToastContainer";
import { createApiClient } from "../../../api/apiClient";
import { AccessibleModuleItem, getAccessibleModules } from "../../../api/ticketApi";
import {
  CATEGORY_OPTIONS,
  LoadImpact,
  MARKET_OPTIONS,
  SubmitTicketFieldErrorMap,
  SUBCATEGORY_OPTIONS,
  SubmitTicketDynamicField,
  SubmitTicketDraft,
  TicketLevel,
  UrgencyLevel,
} from "../../../store/slices/ticketingPortalSlice";
import styles from "../SubmitTicketScreenStyles";
import mobileStyles from "../SubmitTicketScreen.mobile.style";
import SelectField from "./SelectField";
import RepairReplaceReturnGuide from "./RepairReplaceReturnGuide";
import RepairReplaceReturnEquipmentSection from "./RepairReplaceReturnEquipmentSection";
import MultiSelectFieldTemplate from "./fieldTemplates/MultiSelectFieldTemplate";
import AccessRequestLocationPicker, {
  LocationPickerType,
  LocationRegion,
} from "./AccessRequestLocationPicker";
import LhDatePicker from "../../../components/LhDatePicker";
import useSupportTierRoles from "../../Admin/pages/UserManagement/hooks/useSupportTierRoles";

import SelectStakeholdersModal from "./SelectStakeholdersModal";
import { TicketingLocationUser } from "../../../api/ticketApi";

// ── Access Request static data ────────────────────────────────────────────────

const STATIC_ACCESSIBLE_MODULES = [
  "Dashboard",
  "Observations",
  "Leadership Walk",
  "Ticketing Portal",
];

// Fallback module list for Case-2 users (no Navi access) — used when the backend
// does not return staticOptions for the navigator_module trigger field.
// Sourced from MODULE_LABEL_MAP in LhHeader.tsx (API moduleName values).
const NAVIGATOR_MODULE_FALLBACK_OPTIONS = [
  "Action Plan",
  "Analytics",
  "Config",
  "Delivery RPM",
  "DLO",
  "Employee Update",
  "Leadership Walk",
  "NHO",
  "Observations",
  "Perfect Truck",
  "Quality Audit",
  "Racking Inspection",
  "SPARC Walk",
  "Shift Handover",
  "Site Evaluation",
  "Stand Up",
].sort();

type ARUser = { name: string; gpid: string; email: string };

type EmployeeAutocompleteItem = {
  // Fields returned by /admin/user-basic-access/search
  userEmail?: string;
  gpid?: string;
  status?: boolean;
  team?: string;
  // Legacy / fallback fields kept for compatibility
  gpId?: string;
  gp_id?: string;
  id?: string;
  employeeId?: string;
  name?: string;
  displayName?: string;
  firstName?: string;
  first_name?: string;
  givenName?: string;
  lastName?: string;
  last_name?: string;
  surname?: string;
  email?: string;
  mail?: string;
  userPrincipalName?: string;
  user_principal_name?: string;
};

type AccessRequestFormState = {
  requestedForName: string;
  requestedForEmail: string;
  requestedForGpid: string;
  organization: string;
  department: string;
  role: string;
  roleId: number | null;
  roleScopeType: "full" | "region" | "market" | "site" | null;
  selectedLocations: string[];
  accessEndDate: Date | null;
  justification: string;
  currentRole: string;
  subCategory: string;
  /** site name → database numeric id from /admin/locations for correct locationId in submission */
  locationIdMap?: Record<string, string>;
};

// Support roles per story 367227 — all global-scope/full-access, no Organization or Department.
export const SUPPORT_ROLE_NAMES = ["Super User", "Developer", "External Equipment Vendor", "External Equipment Vendor - Dematic", "External Equipment Vendor - Peak", "PBNA Sustain Team Member", "PQGC E-Business Member"] as const;
export const AR_ACCESS_TYPES = ["Normal User", ...SUPPORT_ROLE_NAMES] as const;
// Access Type Tier (story 367227/317066) — static, same reasoning as AR_ACCESS_TYPES above:
// only 3 fixed values, not role data, so no API call needed to list them.
export const ACCESS_TYPE_TIERS = ["Field", "3PL/Outside Labor", "Support"] as const;
const isSupportRoleName = (name?: string | null): boolean =>
  !!name && (SUPPORT_ROLE_NAMES as readonly string[]).includes(name);
export const AR_ORGANIZATIONS = ["PBNA", "FLNA", "1NA"] as const;
export const AR_DEPARTMENTS = [
  "Warehouse",
  "Manufacturing",
  "Delivery",
  "Warehouse Delivered",
] as const;

const AR_DEPARTMENTS_SORTED = [...AR_DEPARTMENTS].sort((a, b) => a.localeCompare(b));

// Valid departments per organization — matches UserAddModal.tsx / UserDetailPage.tsx.
// Not every org supports every department; offering an invalid combo here leads to
// incomplete (or empty) role/location results downstream, so the Department dropdown
// must be scoped per organization instead of always showing the full flat list.
export const AR_DEPARTMENTS_BY_ORG: Record<string, string[]> = {
  PBNA: ["Warehouse", "Manufacturing", "Delivery", "Warehouse Delivered"],
  FLNA: ["Warehouse Delivered", "Manufacturing", "Delivery"],
  "1NA": ["Warehouse", "Delivery"],
};

// Roles per user story — Delivery has its own distinct set
const WAREHOUSE_ROLES = [
  "COE",
  "Region / Division Leader",
  "Market Leader",
  "Site Leader",
  "Site Supervisor",
  "Team Lead",
  "Certifier",
  "Checker",
  "Inventory Coordinator",
];
const DELIVERY_ROLES = [
  "Delivery Driver",
  "Delivery District Leader",
  "Area Delivery Manager",
  "Delivery Market Director",
  "Delivery Market Leader",
  "Delivery Senior Leadership",
  "Delivery COE",
];

export const AR_DEPARTMENT_ROLES: Record<string, string[]> = {
  Warehouse:             WAREHOUSE_ROLES,
  Manufacturing:         WAREHOUSE_ROLES,
  "Warehouse Delivered": WAREHOUSE_ROLES,
  Delivery:              DELIVERY_ROLES,
};

type RoleScopeType = "full" | "region" | "market" | "site";

export const ROLE_SCOPE_MAP: Record<string, { type: RoleScopeType; scopeLabel: string; accessLabel: string; accessColor: string }> = {
  // Warehouse / Manufacturing / Warehouse Delivered roles
  "Super User":                       { type: "full",   scopeLabel: "All locations globally.",                         accessLabel: "Full Access",    accessColor: "#005cbc" },
  "Developer":                       { type: "full",   scopeLabel: "All locations globally.",                         accessLabel: "Full Access",    accessColor: "#005cbc" },
  "External Equipment Vendor":  { type: "full",   scopeLabel: "All locations globally.",                         accessLabel: "Full Access",    accessColor: "#005cbc" },
  "PBNA Sustain Team Member":  { type: "full",   scopeLabel: "All locations globally.",                         accessLabel: "Full Access",    accessColor: "#005cbc" },
  "PQGC E-Business Member":    { type: "full",   scopeLabel: "All locations globally.",                         accessLabel: "Full Access",    accessColor: "#005cbc" },
  "COE":                       { type: "full",   scopeLabel: "All locations globally.",                         accessLabel: "Full Access",    accessColor: "#005cbc" },
  "Region / Division Leader":  { type: "region", scopeLabel: "All locations within one region.",                accessLabel: "Full Access",    accessColor: "#005cbc" },
  "Market Leader":             { type: "market", scopeLabel: "All locations within one market.",                accessLabel: "Limited Access", accessColor: "#cc6e16" },
  "Site Leader":               { type: "site",   scopeLabel: "Assigned site(s) only.",                          accessLabel: "Limited Access", accessColor: "#cc6e16" },
  "Site Supervisor":           { type: "site",   scopeLabel: "Assigned site(s) only.",                          accessLabel: "Limited Access", accessColor: "#cc6e16" },
  "Team Lead":                 { type: "site",   scopeLabel: "Assigned site(s) only.",                          accessLabel: "Limited Access", accessColor: "#cc6e16" },
  "Certifier":                 { type: "site",   scopeLabel: "Assigned site(s) only - field execution views.",  accessLabel: "Limited Access", accessColor: "#cc6e16" },
  "Checker":                   { type: "site",   scopeLabel: "Assigned site(s) only - field execution views.",  accessLabel: "Limited Access", accessColor: "#cc6e16" },
  "Inventory Coordinator":     { type: "site",   scopeLabel: "Assigned site(s) only - field execution views.",  accessLabel: "Limited Access", accessColor: "#cc6e16" },
  // Delivery roles
  "Delivery Driver":           { type: "site",   scopeLabel: "Assigned site(s) only.",                          accessLabel: "Limited Access", accessColor: "#cc6e16" },
  "Delivery District Leader":  { type: "site",   scopeLabel: "Assigned site(s) only.",                          accessLabel: "Limited Access", accessColor: "#cc6e16" },
  "Area Delivery Manager":     { type: "market", scopeLabel: "All locations within one market.",                accessLabel: "Limited Access", accessColor: "#cc6e16" },
  "Delivery Market Director":  { type: "market", scopeLabel: "All locations within one market.",                accessLabel: "Limited Access", accessColor: "#cc6e16" },
  // Alias of "Delivery Market Director" (same backend role id) — must stay in sync with that entry.
  "Delivery Market Leader":    { type: "market", scopeLabel: "All locations within one market.",                accessLabel: "Limited Access", accessColor: "#cc6e16" },
  "Delivery Senior Leadership":{ type: "region", scopeLabel: "All locations within one region.",                accessLabel: "Full Access",    accessColor: "#005cbc" },
  // Delivery-side equivalent of "COE" — full global access.
  "Delivery COE":              { type: "full",   scopeLabel: "All locations globally.",                         accessLabel: "Full Access",    accessColor: "#005cbc" },
};

// Derive initials from full name
const getInitials = (name: string) =>
  name.split(" ").slice(0, 2).map((w) => w[0] ?? "").join("").toUpperCase();

const formatArUserMeta = (user: ARUser) => {
  const gpid = user.gpid?.trim() || "-";
  const email = user.email?.trim() || "-";
  return `GPID ${gpid} - ${email}`;
};

const extractEmployeeAutocompleteRows = (payload: any): EmployeeAutocompleteItem[] => {
  const root = payload?.data ?? payload;
  const source =
    root?.data?.processedData ??
    root?.processedData ??
    root?.data?.content ??
    root?.content ??
    root?.data ??
    root;

  return Array.isArray(source) ? (source as EmployeeAutocompleteItem[]) : [];
};

const normalizeArUser = (row: EmployeeAutocompleteItem): ARUser => {
  const fullName = String(row.name ?? row.displayName ?? "").trim();
  const firstName = String(row.first_name ?? row.firstName ?? row.givenName ?? "").trim();
  const lastName = String(row.last_name ?? row.lastName ?? row.surname ?? "").trim();
  const derivedName = [firstName, lastName].filter(Boolean).join(" ").trim();

  return {
    name: fullName || derivedName,
    gpid: String(row.gpid ?? row.gpId ?? row.gp_id ?? row.employeeId ?? row.id ?? "").trim(),
    email: String(row.userEmail ?? row.email ?? row.mail ?? row.userPrincipalName ?? row.user_principal_name ?? "").trim(),
  };
};

type ApiRoleOption = { id: number | string; name: string; scopeType?: string };
export type { ApiRoleOption };

const normalizeRoleName = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "");

// Maps the backend's scopeType (GLOBAL/REGION/MARKET/SITE) to the picker/location scope type.
export const mapScopeTypeToRoleScope = (scopeType?: string | null): RoleScopeType | null => {
  switch (String(scopeType ?? "").trim().toUpperCase()) {
    case "GLOBAL": return "full";
    case "REGION": return "region";
    case "MARKET": return "market";
    case "SITE": return "site";
    default: return null;
  }
};

// Generic scope label/access-badge config per scope type — no per-role-name hardcoding.
export const buildScopeConfigFromType = (type: RoleScopeType) => {
  switch (type) {
    case "full":
      return { type, scopeLabel: "All locations globally.", accessLabel: "Full Access", accessColor: "#005cbc" };
    case "region":
      return { type, scopeLabel: "All locations within one region.", accessLabel: "Full Access", accessColor: "#005cbc" };
    case "market":
      return { type, scopeLabel: "All locations within one market.", accessLabel: "Limited Access", accessColor: "#cc6e16" };
    case "site":
      return { type, scopeLabel: "Assigned site(s) only.", accessLabel: "Limited Access", accessColor: "#cc6e16" };
  }
};

// Derives a role's scope purely from the scopeType returned by /admin/users/roles —
// callers must supply the live roles list for the relevant department(s).
export const getRoleScopeConfig = (roleName: string, roles: ApiRoleOption[] = []) => {
  const normalized = normalizeRoleName(roleName);
  const apiMatch = roles.find((role) => normalizeRoleName(role.name) === normalized);
  const apiScopeType = apiMatch ? mapScopeTypeToRoleScope(apiMatch.scopeType) : null;
  if (apiScopeType) return buildScopeConfigFromType(apiScopeType);
  // Support-tier roles have no department, so they never appear in the department roles list —
  // they are always global/full-access.
  return isSupportRoleName(roleName) ? buildScopeConfigFromType("full") : null;
};

// Per-role distinct-region cap for the "market" scope picker (mirrors the
// maxSiteSelections cap used for "site" scope roles). Undefined = no cap.
const ROLE_REGION_CAPS: Record<string, number> = {
  [normalizeRoleName("Market Lead")]: 2,
};
export const getMaxRegionSelectionsForRole = (roleName?: string | null): number | undefined =>
  roleName ? ROLE_REGION_CAPS[normalizeRoleName(roleName)] : undefined;

export const extractRoleOptionsFromResponse = (payload: any): ApiRoleOption[] => {
  const rawRoles = Array.isArray(payload)
    ? payload
    : payload?.content ?? payload?.data ?? payload?.roles ?? [];

  if (!Array.isArray(rawRoles)) {
    return [];
  }

  return rawRoles
    .map((role: any): ApiRoleOption | null => {
      const name = String(role?.name ?? role?.roleName ?? role?.ROLE_NAME ?? "").trim();
      const id = role?.id ?? role?.roleId ?? role?.ROLE_ID ?? name;
      const scopeType = String(role?.scopeType ?? role?.SCOPE_TYPE ?? role?.scope_type ?? "").trim();
      return name ? { id, name, scopeType } : null;
    })
    .filter((role): role is ApiRoleOption => Boolean(role));
};

type Props = {
  draft: SubmitTicketDraft;
  submitter?: string;
  categoryOptions?: readonly string[] | string[];
  subCategoryMap?: Record<string, string[]>;
  /** Tertiary category names for the currently selected category + sub-category; empty when none configured. */
  tertiaryCategoryOptions?: string[];
  triggerFields?: SubmitTicketDynamicField[];
  triggerValues?: Record<string, string>;
  triggerFieldOptions?: Record<string, string[]>;
  /** All distinct location names — for from_location / to_location trigger fields */
  locationNames?: string[];
  /** Location names that have a valid locSapId — for Ticket Level = Location */
  locationsWithSapId?: string[];
  /** Distinct regions — for Ticket Level = Region */
  regionOptions?: string[];
  /** Distinct markets — for Ticket Level = Market */
  marketOptions?: string[];
  /** Accessible module names from appData.moduleAccess — for navigator_module trigger field */
  moduleOptions?: string[];
  /** Optional labels to render for location options (e.g. Name (Location ID)). */
  locationOptionLabels?: Record<string, string>;
  /** Maps location name → { locationId, sapId } for multi-field search in location dropdown */
  locationSearchMetadata?: Record<string, { locationId: string; sapId: string }>;
  /** User role for COE/Super User features */
  userRole?: string;
  /** All locations for National Issue feature */
  submitLocationRows?: Array<{ locName: string; locId: string; locSapId: string; region: string; market: string }>;
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onStakeholdersChange: (value: string) => void;
  onSubmitterChange: (value: string) => void; // GAP-01
  isLimitedAccess?: boolean; // GAP-01: true = editable submitter (non-SSO / limited-access URL flow)
  onFieldBlur?: (fieldName: string) => void; // GAP-02
  onCategoryChange: (value: string) => void;
  onSubCategoryChange: (value: string) => void;
  onTertiaryCategoryChange: (value: string) => void;
  onLevelChange: (value: TicketLevel) => void;
  onLevelReferenceChange: (value: string) => void;
  onUrgencyChange: (value: UrgencyLevel) => void;
  onImpactedPeopleChange: (value: string) => void;
  onLoadImpactChange: (value: LoadImpact) => void;
  onMmsToggleChange: (value: boolean) => void;
  dropdownResetKey?: number;
  onTriggerFieldChange: (fieldName: string, value: string) => void;
  onAccessRequestStateChange?: (state: AccessRequestFormState) => void;
  hideUrgencyField?: boolean;
  fieldErrors?: SubmitTicketFieldErrorMap;
  isSubmitting?: boolean;
  isSubmitDisabled?: boolean;
  onAddAttachment: () => void;
  onRemoveAttachment: (attachmentId: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  mobile?: boolean;
};

/** Imperative handle so the parent can flush any un-added stakeholder text right before submit. */
export type SubmitTicketFormPanelHandle = {
  flushStakeholderInput: () => string[];
  /** Runs equipment field validation; returns true if valid, false if blocked. */
  validateEquipment: () => boolean;
};

const SubmitTicketFormPanel = forwardRef<SubmitTicketFormPanelHandle, Props>(({
  draft,
  submitter,
  categoryOptions,
  subCategoryMap,
  tertiaryCategoryOptions = [],
  triggerFields,
  triggerValues,
  triggerFieldOptions,
  locationNames = [],
  locationsWithSapId = [],
  regionOptions = [],
  marketOptions = [],
  moduleOptions = [],
  locationOptionLabels,
  locationSearchMetadata,
  userRole,
  submitLocationRows = [],
  fieldErrors = {} as SubmitTicketFieldErrorMap,
  isSubmitting = false,
  isSubmitDisabled = false,
  onTitleChange,
  onDescriptionChange,
  onStakeholdersChange,
  onSubmitterChange,
  isLimitedAccess = false,
  onFieldBlur,
  onCategoryChange,
  onSubCategoryChange,
  onTertiaryCategoryChange,
  onLevelChange,
  onLevelReferenceChange,
  onUrgencyChange,
  onImpactedPeopleChange,
  onLoadImpactChange,
  onMmsToggleChange,
  dropdownResetKey,
  onTriggerFieldChange,
  onAccessRequestStateChange,
  hideUrgencyField = false,
  onAddAttachment,
  onRemoveAttachment,
  onSubmit,
  onCancel,
  mobile,
}, ref) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const md = isDark;
  const toast = useToast();
  const [descriptionHeight, setDescriptionHeight] = useState(120);
  // Locked fields from Redux (used for limited-access prefill + lock UX)
  const lockedFields: { title?: boolean; category?: boolean; subCategory?: boolean; submitter?: boolean } = useSelector((state: any) => state.ticketingPortal?.submitTicketLockedFields ?? {});
  // System-locked field display: bold value + trailing lock icon (no dropdown/input, no "Locked" text tag)
  const LockedValueBox: React.FC<{ value: string }> = ({ value }) => (
    <View style={styles.lockedBox}>
      <Text style={styles.lockedBoxText} numberOfLines={1}>{value}</Text>
      <MaterialIcons name="lock" size={16} color="#005cbc" />
    </View>
  );
  const [textMultiInputs, setTextMultiInputs] = useState<Record<string, string>>({});
  const [stakeholderInput, setStakeholderInput] = useState("");
  const [stakeholderError, setStakeholderError] = useState<string | null>(null);
  const [activeMobileDropdownId, setActiveMobileDropdownId] = useState<string | null>(null);

  // Reset any open dropdown when the parent signals it (e.g. after duplicate modal dismiss)
  useEffect(() => {
    if (dropdownResetKey !== undefined) {
      setActiveMobileDropdownId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownResetKey]);

  const [regionLimitMessage, setRegionLimitMessage] = useState<string | null>(null);

  // Handler for region selection with COE exclusivity rule
  const handleRegionSelection = (value: string) => {
    try {
      const parsed = JSON.parse(value);
      const selectedArray = Array.isArray(parsed) ? parsed : [parsed];
      
      // Check if COE is in the selection
      const hasCOE = selectedArray.includes("COE");
      const hasOtherRegions = selectedArray.some((r: string) => r !== "COE");
      
      // If both COE and other regions are selected, keep only COE
      if (hasCOE && hasOtherRegions) {
        setRegionLimitMessage("COE can only be selected alone. Other regions have been removed.");
        onLevelReferenceChange(JSON.stringify(["COE"]));
        return;
      } 
      // If COE is being selected alone, that's valid
      if (hasCOE && selectedArray.length === 1) {
        setRegionLimitMessage(null);
        onLevelReferenceChange(JSON.stringify(selectedArray));
        return;
      }
            
      // If only other regions (no COE), allow them
      if (hasOtherRegions && !hasCOE) {
        setRegionLimitMessage(null);
        onLevelReferenceChange(JSON.stringify(selectedArray));
        return;
      }
           
      // Default case (empty selection)
      setRegionLimitMessage(null);
      onLevelReferenceChange(value);
    } catch {
      onLevelReferenceChange(value);
    }
  };

  // ?? Redux selectors ???????????????????????????????????????????????????????????
  const allLocations = useSelector((state: any) => state.ticketingPortal.allLocations ?? []);
  // Current role from /common/location-with-access stored in Redux appData
  const reduxUserRoleName: string = useSelector((state: any) => state.appData?.userInfo?.roleName ?? "");
  const reduxRoles: Array<{ ROLE_ID?: number | string; ROLE_NAME?: string; id?: number | string; name?: string }> =
    useSelector((state: any) => state.appData?.roles ?? []);
  const appDataUserGpid: string = useSelector((state: any) => state.appData?.userInfo?.GPID ?? "");
  // /admin/user-basic-access is fetched for ALL authenticated users (see AppWrapper.tsx) and
  // reliably returns gpid even when location-with-access userInfo doesn't (e.g. contractors).
  const basicAccessUserGpid: string = useSelector((state: any) => state.appData?.basicAccessUser?.gpid ?? "");
  const noAccessMode: boolean = useSelector((state: any) => state.appData?.noAccessMode === true);
  // Signed-in user info for defaulting Requested For
  const authUserName: string = useSelector((state: any) => state.auth?.user?.name ?? state.auth?.user?.claims?.name ?? "");
  const authUserEmail: string = useSelector((state: any) => state.auth?.user?.username ?? "");
  const authUserGpid: string = useSelector((state: any) =>
    appDataUserGpid ||
    basicAccessUserGpid ||
    state.auth?.user?.gpid ||
    state.auth?.user?.claims?.employeeId ||
    state.auth?.user?.employeeId ||
    state.auth?.user?.id ||
    ""
  );

  // Build location tree from Redux allLocations (from /api/common/all-locations)
  const locationTree = useMemo(() => {
    if (!allLocations.length) return [];
    const regionMap = new Map<string, Map<string, Set<string>>>();
    for (const loc of allLocations) {
      const reg = loc.region ?? "Unknown";
      const mkt = loc.market ?? "Unknown";
      const site = loc.locName ?? "";
      if (!regionMap.has(reg)) regionMap.set(reg, new Map());
      const mktMap = regionMap.get(reg)!;
      if (!mktMap.has(mkt)) mktMap.set(mkt, new Set());
      if (site) mktMap.get(mkt)!.add(site);
    }
    return Array.from(regionMap.entries()).map(([region, mktMap]) => ({
      region,
      siteCount: Array.from(mktMap.values()).reduce((s, sites) => s + sites.size, 0),
      markets: Array.from(mktMap.entries()).map(([market, sites]) => ({
        market,
        siteCount: sites.size,
        sites: Array.from(sites).map((name) => ({ name })),
      })),
    }));
  }, [allLocations]);

  // ── Access Request local state ───────────────────────────────────────────────
  const [arRequestedForQuery, setArRequestedForQuery] = useState("");
  // Default Requested For to the signed-in user
  const [arRequestedForUser, setArRequestedForUser] = useState<ARUser | null>(() =>
    authUserName || authUserEmail
      ? { name: authUserName || authUserEmail, email: authUserEmail, gpid: authUserGpid }
      : null
  );
  // Backfill gpid once Redux (e.g. basicAccessUser) resolves it after this component's initial mount,
  // but only while Requested For still points at the signed-in user (no manual search selection made).
  useEffect(() => {
    if (!authUserGpid) return;
    setArRequestedForUser((prev) => {
      if (!prev || prev.gpid || prev.email !== authUserEmail) return prev;
      return { ...prev, gpid: authUserGpid };
    });
  }, [authUserGpid, authUserEmail]);
  const [arSearchResults, setArSearchResults] = useState<ARUser[] | null>(null);
  const [arSearchLoading, setArSearchLoading] = useState(false);
  const [arOrganization, setArOrganization] = useState<string>("");
  const [arDepartment, setArDepartment] = useState<string>("");
  const [arRole, setArRole] = useState<string>("");
  // Access Type Tier (story 367227/317066) — Support bypasses Organization/Department entirely.
  const [arAccessTypeTier, setArAccessTypeTier] = useState<typeof ACCESS_TYPE_TIERS[number]>('Field');
  const { supportTierRoles: arSupportTierRoles, supportTierRolesLoading: arSupportTierRolesLoading } = useSupportTierRoles('access-request');
  // Scope the Department dropdown to the selected organization; falls back to the full
  // list only when no organization is picked yet (keeps behavior consistent across orgs).
  const arDepartmentOptions = useMemo(
    () => (arOrganization ? AR_DEPARTMENTS_BY_ORG[arOrganization] ?? AR_DEPARTMENTS_SORTED : AR_DEPARTMENTS_SORTED),
    [arOrganization]
  );
  const [arJustification, setArJustification] = useState<string>("");
  const [arSelectedLocations, setArSelectedLocations] = useState<string[]>([]);
  // Maps site display-name → database numeric id (from /admin/locations `id`) for correct
  // locationId values in the submission payload.
  const [arLocationIdMap, setArLocationIdMap] = useState<Record<string, string>>({});
  const [arDropdownOpen, setArDropdownOpen] = useState<string | null>(null);
  const [arAccessEndDate, setArAccessEndDate] = useState<Date | null>(null);
  const [arFetchedRoles, setArFetchedRoles] = useState<ApiRoleOption[]>([]);
  const [accessibleModules, setAccessibleModules] = useState<AccessibleModuleItem[]>([]);
  const [accessibleModulesLoading, setAccessibleModulesLoading] = useState(false);

  // ── Dynamically fetched location tree (New Access / Temporary Access) ────────
  const [arFetchedLocations, setArFetchedLocations] = useState<LocationRegion[]>([]);
  const [arLocationsLoading, setArLocationsLoading] = useState(false);

  // ── Modify Access: current role fetched for the selected user ─────────────────
  const [arModifyCurrentRole, setArModifyCurrentRole] = useState<string>("");
  // Pending location names (from /admin/users) to pre-select once the location tree is ready
  const [arPendingPrePopulate, setArPendingPrePopulate] = useState<string[] | null>(null);

  const isAccessRequest = draft.category === "Access Request";
  const isModifyAccess  = isAccessRequest && draft.subCategory === "Modify Access";
  const isTemporaryAccess = isAccessRequest && draft.subCategory === "Request Temporary Access";
  const isRepairReplaceReturn = draft.category === "Repair / Replace / Return";

  const arTempMaxDate = React.useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d;
  }, []);

  // Current role: for Modify/Temporary Access use the API-fetched role, else fall back to Redux
  const arCurrentRole = (isModifyAccess || isTemporaryAccess)
    ? (arModifyCurrentRole || reduxUserRoleName || "")
    : (reduxUserRoleName || "");

  const arRoleOptions = arAccessTypeTier === 'Support'
    ? arSupportTierRoles.map((r) => r.roleName)
    : (arDepartment ? arFetchedRoles.map((role) => role.name) : []);
  const roleScope       = arRole ? getRoleScopeConfig(arRole, arFetchedRoles) : null;
  const showLocationPicker =
    (arRole !== "" && roleScope != null) ||
    (isModifyAccess && arFetchedLocations.length > 0);

  const explicitRoleIdByLabel: Record<string, number> = {
    "Super User": 1,
    SuperUser: 1,
    "Super user": 1,
    COE: 2,
    CoE: 2,
    "Region / Division Leader": 3,
    "Region Leader/Division": 3,
    "Site Leader": 4,
    "Site Supervisor": 5,
    Checker: 6,
    Certifier: 7,
    "Inventory Coordinator": 8,
    "Team Lead": 9,
    Developer: 15,
    // Delivery department roles
    "Delivery Driver": 11,
    "Delivery District Leader": 12,
    "Area Delivery Manager": 13,
    "Delivery Market Director": 14,
    "Delivery Market Leader": 14,
    "Delivery Senior Leadership": 16,
    "Delivery COE": 17,
  };

  const roleNameAliases: Record<string, string[]> = {
    "Region / Division Leader": ["Region Leader/Division"],
    "Super User": ["SuperUser", "Super user"],
  };

  useEffect(() => {
    if (!isAccessRequest || !arDepartment) {
      setArFetchedRoles([]);
      return;
    }

    let cancelled = false;

    const fetchDepartmentRoles = async () => {
      try {
        const api = createApiClient();
        const response = await api.get("/admin/users/roles", {
          params: { department: arDepartment },
        });

        if (cancelled) {
          return;
        }

        setArFetchedRoles(extractRoleOptionsFromResponse(response.data));
      } catch {
        if (!cancelled) {
          setArFetchedRoles([]);
        }
      }
    };

    void fetchDepartmentRoles();

    return () => {
      cancelled = true;
    };
  }, [arDepartment, isAccessRequest]);

  // ── Auto-populate org/dept/role/locations for Modify Access and Temporary Access ───
  // When a user is selected, fetch their existing org/dept/role and pre-populate the form
  useEffect(() => {
    if ((!isModifyAccess && !isTemporaryAccess) || !arRequestedForUser) {
      return;
    }

    const userEmail = arRequestedForUser?.email?.trim();
    const userGpid = arRequestedForUser?.gpid?.trim();
    
    console.log('[SubmitTicketFormPanel] Starting org/dept fetch for:', { 
      email: userEmail,
      gpid: userGpid,
      isModifyAccess
    });
    
    if (!userEmail && !userGpid) {
      console.warn('[SubmitTicketFormPanel] No email or GPID available');
      return;
    }

    let cancelled = false;

    const fetchUserOrgDept = async () => {
      try {
        const api = createApiClient();

        // Use the same /admin/users search API as User Management screen
        // so the role displayed matches what admins see in User Management
        const searchTerm = userEmail || userGpid || "";
        console.log('[SubmitTicketFormPanel] Fetching user via /admin/users search:', searchTerm);

        const response = await api.get('/admin/users', {
          params: { search: searchTerm, page: 0, pageSize: 10 },
          _skipLocationContext: true,
        } as any);

        console.log('[SubmitTicketFormPanel] API Response:', response.data);

        if (cancelled) return;

        // Response is { data: [...] } or plain array
        const users: any[] = response.data?.data ?? (Array.isArray(response.data) ? response.data : []);
        // Find the matching user by email or gpid
        const userData = users.find(
          (u: any) =>
            (userEmail && u.userEmail?.toLowerCase() === userEmail.toLowerCase()) ||
            (userGpid && u.gpid === userGpid)
        ) ?? users[0] ?? null;

        console.log('[SubmitTicketFormPanel] Matched user data:', userData);

        if (userData) {
          const org = userData.organization || userData.organizationType || "";
          const dept = userData.department || "";
          // 'role' is the access level field returned by /admin/users
          const currentRole = userData.role || userData.accessLevel || "";

          console.log('[SubmitTicketFormPanel] ✅ org/dept/role:', { org, dept, currentRole });

          const currentIsSupportRole = isSupportRoleName(currentRole);
          setArAccessTypeTier(currentIsSupportRole ? 'Support' : 'Field');
          setArOrganization(currentIsSupportRole ? "" : org);
          setArDepartment(currentIsSupportRole ? "" : dept);
          setArModifyCurrentRole(currentRole);

          // Pre-select the user's current role in the Role dropdown
          if (currentRole) {
            setArRole(currentRole);
          }

          // For non-full-access roles, queue the user's current location assignments
          // for pre-selection once the location tree finishes loading.
          const roleScopeConfig = currentRole ? getRoleScopeConfig(currentRole, arFetchedRoles) : null;
          if (roleScopeConfig?.type !== "full" && currentRole) {
            const rawLocs: any[] =
              userData.locations ?? userData.sites ?? userData.userLocations ?? [];
            const extractedNames: string[] = Array.isArray(rawLocs)
              ? rawLocs
                  .map((loc: any) =>
                    String(
                      loc.locationName ?? loc.location ?? loc.locName ?? loc.name ?? loc.site ?? ""
                    ).trim()
                  )
                  .filter(Boolean)
              : [];
            if (!extractedNames.length) {
              const single = String(
                userData.locationName ?? userData.location ?? userData.site ?? userData.locName ?? ""
              ).trim();
              if (single) extractedNames.push(single);
            }
            setArPendingPrePopulate(extractedNames.length > 0 ? extractedNames : null);
          } else {
            // Full-access roles: existing roleScope effect handles selecting all locations
            setArPendingPrePopulate(null);
          }
        } else {
          console.warn('[SubmitTicketFormPanel] ⚠️ User not found in search results - clearing fields');
          setArOrganization("");
          setArDepartment("");
          setArModifyCurrentRole("");
          setArPendingPrePopulate(null);
        }
      } catch (error) {
        console.error('[SubmitTicketFormPanel] ❌ Failed to fetch user data:', error);

        if (cancelled) return;

        const status = (error as any)?.response?.status;
        if (status === 404) {
          toast.showToast({
            header: 'User Not Found',
            subtext: 'This user does not exist in the system. Use "New Access" instead to create access for a new user.',
            variant: 'error',
            position: 'top',
          });
          setArRequestedForUser(null);
        }
        setArOrganization("");
        setArDepartment("");
        setArModifyCurrentRole("");
        setArPendingPrePopulate(null);
      }
    };

    void fetchUserOrgDept();
    return () => { cancelled = true; };
  }, [isModifyAccess, isTemporaryAccess, arRequestedForUser?.email]);

  // Apply pre-populated location selections once the location tree is ready
  useEffect(() => {
    if (!arPendingPrePopulate || arPendingPrePopulate.length === 0) return;
    if (arLocationsLoading) return;
    if (!arFetchedLocations.length) return;

    const allSiteNames = new Set(
      arFetchedLocations.flatMap((reg) =>
        reg.markets.flatMap((mkt) =>
          mkt.sites.map((site) => site.name.toLowerCase())
        )
      )
    );
    const matched = arPendingPrePopulate.filter((name) =>
      allSiteNames.has(name.toLowerCase())
    );
    setArSelectedLocations(matched);
    setArPendingPrePopulate(null);
  }, [arFetchedLocations, arLocationsLoading, arPendingPrePopulate]);

  // ── Fetch department roles when department is selected ──────────────────────
  useEffect(() => {
    if (!isAccessRequest || !arDepartment) {
      setArFetchedRoles([]);
      return;
    }

    let cancelled = false;

    const fetchDepartmentRoles = async () => {
      try {
        const api = createApiClient();
        const response = await api.get("/admin/users/roles", {
          params: { department: arDepartment },
        });

        if (cancelled) {
          return;
        }

        setArFetchedRoles(extractRoleOptionsFromResponse(response.data));
      } catch {
        if (!cancelled) {
          setArFetchedRoles([]);
        }
      }
    };

    void fetchDepartmentRoles();
    return () => { cancelled = true; };
  }, [arDepartment, isAccessRequest]);

  const resolveRoleId = useCallback((roleName: string): number | null => {
    const normalizedSelectedRole = normalizeRoleName(roleName);

    const fetchedRoleMatch = arFetchedRoles.find((role) => {
      const candidateNames = [
        role.name,
        String(role.id ?? ""),
        ...(roleNameAliases[roleName] ?? []),
      ];

      return candidateNames
        .filter(Boolean)
        .map(normalizeRoleName)
        .some((candidate) => candidate === normalizedSelectedRole);
    });

    if (fetchedRoleMatch) {
      const fetchedRoleId = Number(fetchedRoleMatch.id);
      if (Number.isFinite(fetchedRoleId)) {
        return fetchedRoleId;
      }
    }

    // Support-tier roles (Access Type Tier = "Support") skip Organization/Department, so
    // arFetchedRoles is always empty for them — resolve their roleId from arSupportTierRoles instead.
    const supportTierRoleMatch = arSupportTierRoles.find((role) => {
      const candidateNames = [role.roleName, ...(roleNameAliases[roleName] ?? [])];
      return candidateNames
        .map(normalizeRoleName)
        .some((candidate) => candidate === normalizedSelectedRole);
    });
    if (supportTierRoleMatch) {
      const supportTierRoleId = Number(supportTierRoleMatch.roleId);
      if (Number.isFinite(supportTierRoleId)) { return supportTierRoleId; }
    }

    const explicitRoleCandidates = [
      roleName,
      ...((roleNameAliases[roleName] ?? []) as string[]),
    ];

    for (const candidate of explicitRoleCandidates) {
      const explicitRoleId = explicitRoleIdByLabel[candidate];
      if (explicitRoleId != null) {
        return explicitRoleId;
      }
    }

    const matchedRole = reduxRoles.find((role) => {
      const backendRoleName = String(role.ROLE_NAME ?? role.name ?? "");
      const candidateNames = [backendRoleName, ...(roleNameAliases[roleName] ?? [])];
      return candidateNames
        .map(normalizeRoleName)
        .some((candidate) => candidate === normalizedSelectedRole);
    });

    const rawRoleId = matchedRole?.ROLE_ID ?? matchedRole?.id ?? null;
    const numericRoleId = rawRoleId == null ? null : Number(rawRoleId);
    return Number.isFinite(numericRoleId) ? numericRoleId : null;
  }, [arFetchedRoles, arSupportTierRoles, reduxRoles]);

  const selectedRoleId = useMemo(() => {
    if (!arRole.trim()) {
      return null;
    }

    return resolveRoleId(arRole);
  }, [arRole, resolveRoleId]);

  const allRegionSelections = useMemo(() => {
    const tree = arFetchedLocations.length > 0 ? arFetchedLocations : locationTree;
    const regions = tree.map((region) => region.region);
    return Array.from(new Set(regions));
  }, [locationTree, arFetchedLocations]);

  const allSiteSelections = useMemo(() => {
    const tree = arFetchedLocations.length > 0 ? arFetchedLocations : locationTree;
    return Array.from(
      new Set(
        tree.flatMap((region) =>
          region.markets.flatMap((market) => market.sites.map((site) => site.name))
        )
      )
    );
  }, [locationTree, arFetchedLocations]);

  // ── Fetch locations from /admin/locations when role + org are known (New/Temp) ─
  useEffect(() => {
    if (isModifyAccess) return; // Modify Access uses a different data source
    if (!arRole || !arOrganization) {
      setArFetchedLocations([]);
      setArLocationIdMap({});
      return;
    }
    let cancelled = false;
    const fetchOrgLocations = async () => {
      setArLocationsLoading(true);
      try {
        const api = createApiClient();
        // Single request, organizationType-scoped — mirrors User Management's location fetch.
        const res = await api.get("/admin/locations", {
          params: { organizationType: arOrganization, page: 0, pageSize: 2000 },
        });
        if (cancelled) return;
        const body = res.data;
        const rows: any[] = Array.isArray(body) ? body : (body?.content ?? body?.data?.content ?? body?.data ?? []);
        // Build region → market → site tree; key by locId (mirrors Analytics Hub)
        // so two entries with the same locName but different locId both appear.
        const regionMap = new Map<string, Map<string, Map<string, string>>>();
        for (const loc of rows) {
          const reg = loc.region ?? "Unknown";
          const mkt = loc.market ?? "Unknown";
          const siteId = String(loc.locId ?? loc.id ?? "").trim();
          const siteName = String(loc.locName ?? "").trim();
          const key = siteId || siteName;
          if (!key) continue;
          if (!regionMap.has(reg)) regionMap.set(reg, new Map());
          const mktMap = regionMap.get(reg)!;
          if (!mktMap.has(mkt)) mktMap.set(mkt, new Map());
          mktMap.get(mkt)!.set(key, siteName || siteId);
        }
        const tree = Array.from(regionMap.entries()).map(([region, mktMap]) => ({
          region,
          siteCount: Array.from(mktMap.values()).reduce((s, sites) => s + sites.size, 0),
          markets: Array.from(mktMap.entries()).map(([market, sites]) => ({
            market,
            siteCount: sites.size,
            sites: Array.from(sites.values()).map((name) => ({ name })),
          })),
        }));
        // Build site-name → database numeric id map so the submission payload uses the
        // correct `id` from /admin/locations (not the SAP locId string or the site name).
        const idMap: Record<string, string> = {};
        for (const loc of rows) {
          const siteName = String(loc.locName ?? "").trim();
          const dbId = String(loc.id ?? "").trim();
          if (siteName && dbId) idMap[siteName] = dbId;
        }
        setArLocationIdMap(idMap);
        setArFetchedLocations(tree);
      } catch {
        if (!cancelled) {
          setArFetchedLocations([]);
          setArLocationIdMap({});
        }
      } finally {
        if (!cancelled) setArLocationsLoading(false);
      }
    };
    void fetchOrgLocations();
    return () => { cancelled = true; };
  }, [arRole, arOrganization, isModifyAccess]);

  // ── Fetch ALL active locations for Modify Access location picker ─────────────
  // Current role is already set by fetchUserOrgDept (via /admin/users search).
  useEffect(() => {
    if (!isModifyAccess) {
      setArFetchedLocations([]);
      setArLocationIdMap({});
      return;
    }
    if (!arRole) {
      setArFetchedLocations([]);
      setArLocationIdMap({});
      return;
    }
    const userEmail = arRequestedForUser?.email?.trim();
    const userGpid = arRequestedForUser?.gpid?.trim();
    if (!userEmail && !userGpid) {
      setArFetchedLocations([]);
      setArLocationIdMap({});
      return;
    }
    let cancelled = false;
    const fetchAllActiveLocations = async () => {
      setArLocationsLoading(true);
      try {
        const api = createApiClient();
        // Single request, organizationType-scoped — mirrors User Management's location fetch.
        const res = await api.get("/admin/locations", {
          params: { organizationType: arOrganization, page: 0, pageSize: 2000 },
        });
        if (cancelled) return;
        const body = res.data;
        const allRows: any[] = Array.isArray(body) ? body : (body?.content ?? body?.data?.content ?? body?.data ?? []);
        // Key by locId (mirrors Analytics Hub) so two entries with the same
        // locName but different locId both appear — gives the correct 406 count.
        const regionMap = new Map<string, Map<string, Map<string, string>>>();
        for (const loc of allRows) {
          const reg = loc.region ?? "Unknown";
          const mkt = loc.market ?? "Unknown";
          const siteId = String(loc.locId ?? loc.id ?? "").trim();
          const siteName = String(loc.locName ?? loc.name ?? "").trim();
          const key = siteId || siteName;
          if (!key) continue;
          if (!regionMap.has(reg)) regionMap.set(reg, new Map());
          const mktMap = regionMap.get(reg)!;
          if (!mktMap.has(mkt)) mktMap.set(mkt, new Map());
          mktMap.get(mkt)!.set(key, siteName || siteId);
        }
        const tree = Array.from(regionMap.entries()).map(([region, mktMap]) => ({
          region,
          siteCount: Array.from(mktMap.values()).reduce((s, sites) => s + sites.size, 0),
          markets: Array.from(mktMap.entries()).map(([market, sites]) => ({
            market,
            siteCount: sites.size,
            sites: Array.from(sites.values()).map((name) => ({ name })),
          })),
        }));
        // Build site-name → database numeric id map so the submission payload uses the
        // correct `id` from /admin/locations (not the SAP locId string or the site name).
        const idMap: Record<string, string> = {};
        for (const loc of allRows) {
          const siteName = String(loc.locName ?? loc.name ?? "").trim();
          const dbId = String(loc.id ?? "").trim();
          if (siteName && dbId) idMap[siteName] = dbId;
        }
        setArLocationIdMap(idMap);
        setArFetchedLocations(tree);
      } catch {
        if (!cancelled) {
          setArFetchedLocations([]);
          setArLocationIdMap({});
        }
      } finally {
        if (!cancelled) setArLocationsLoading(false);
      }
    };
    void fetchAllActiveLocations();
    return () => { cancelled = true; };
  }, [isModifyAccess, arRequestedForUser?.email, arRequestedForUser?.gpid, arRole, arOrganization]);

  useEffect(() => {
    if (roleScope?.type === "full") {
      setArSelectedLocations(allSiteSelections);
    }
  }, [allSiteSelections, roleScope?.type]);

  // Resolved location tree: prefer the dynamically fetched data, fall back to Redux allLocations
  const activeLocationTree = arFetchedLocations.length > 0 ? arFetchedLocations : locationTree;

  const displaySelectedLocations = roleScope?.type === "full" ? allSiteSelections : arSelectedLocations;

  const loadAccessibleModules = useCallback(async (roleId: number | null) => {
    if (!isAccessRequest) {
      setAccessibleModules([]);
      setAccessibleModulesLoading(false);
      return;
    }

    if (roleId == null || !Number.isFinite(roleId)) {
      setAccessibleModules([]);
      setAccessibleModulesLoading(false);
      return;
    }

    setAccessibleModulesLoading(true);
    try {
      const modules = await getAccessibleModules(roleId);
      setAccessibleModules(modules);
    } finally {
      setAccessibleModulesLoading(false);
    }
  }, [isAccessRequest]);

  useEffect(() => {
    void loadAccessibleModules(selectedRoleId);
  }, [loadAccessibleModules, selectedRoleId]);

  const visibleAccessibleModules = useMemo(() => {
    const seen = new Set<string>();

    return accessibleModules
      .filter((module) => {
        if (!module.hasAccess) {
          return false;
        }

        const dedupeKey = `${module.moduleId}:${module.moduleName.trim().toLowerCase()}`;
        if (seen.has(dedupeKey)) {
          return false;
        }

        seen.add(dedupeKey);
        return true;
      })
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [accessibleModules]);

  // readOnly is missing on some records — default to read-only (view-only) per API convention.
  const fullAccessModules = useMemo(
    () => visibleAccessibleModules.filter((module) => module.readOnly === false),
    [visibleAccessibleModules]
  );
  const viewOnlyModules = useMemo(
    () => visibleAccessibleModules.filter((module) => module.readOnly !== false),
    [visibleAccessibleModules]
  );

  useEffect(() => {
    if (!onAccessRequestStateChange) {
      return;
    }

    onAccessRequestStateChange({
      requestedForName: arRequestedForUser?.name ?? "",
      requestedForEmail: arRequestedForUser?.email ?? "",
      requestedForGpid: arRequestedForUser?.gpid ?? "",
      organization: arOrganization,
      department: arDepartment,
      role: arRole,
      roleId: selectedRoleId,
      roleScopeType: roleScope?.type ?? null,
      selectedLocations: arSelectedLocations,
      accessEndDate: arAccessEndDate,
      justification: arJustification,
      currentRole: arCurrentRole,
      subCategory: draft.subCategory,
      locationIdMap: arLocationIdMap,
    });
  }, [
    onAccessRequestStateChange,
    arRequestedForUser,
    arOrganization,
    arDepartment,
    arRole,
    selectedRoleId,
    // roleScope is recomputed (new object) on every render — depend on the primitive
    // type instead, otherwise this effect re-fires every render and loops forever
    // via the parent's onAccessRequestStateChange setState call.
    roleScope?.type,
    arSelectedLocations,
    arAccessEndDate,
    arJustification,
    arCurrentRole,
    draft.subCategory,
    arLocationIdMap,
  ]);

  const handleArSearch = async (query: string) => {
    setArRequestedForQuery(query);
    if (!query.trim()) {
      setArSearchResults(null);
      return;
    }
    setArSearchLoading(true);
    try {
      const api = createApiClient();
      const res = await api.get("/admin/user-basic-access/search", {
        params: { key: query.trim() },
      });
      const raw = extractEmployeeAutocompleteRows(res?.data);
      const hits: ARUser[] = raw
        .map(normalizeArUser)
        .filter((u) => Boolean(u.email || u.gpid || u.name));
      setArSearchResults(hits);
    } catch {
      setArSearchResults([]);
    } finally {
      setArSearchLoading(false);
    }
  };

  const handleArSelectUser = async (user: ARUser) => {
    // If gpid is missing from search results, fetch full user details
    if (!user.gpid && user.email) {
      try {
        const api = createApiClient();
        const response = await api.get("/admin/user-basic-access/search", {
          params: { key: user.email },
        });
        const raw = extractEmployeeAutocompleteRows(response?.data);
        const enriched = raw.find(
          (u) => String(u.userEmail ?? u.email ?? "").toLowerCase() === user.email.toLowerCase()
        );
        if (enriched) {
          const normalizedEnriched = normalizeArUser(enriched);
          // Merge with original user, preferring enriched gpid
          const mergedUser = {
            ...user,
            gpid: normalizedEnriched.gpid || user.gpid,
          };
          setArRequestedForUser(mergedUser);
          setArRequestedForQuery("");
          setArSearchResults(null);
          return;
        }
      } catch (error) {
        console.warn('[SubmitTicketFormPanel] Failed to enrich user with gpid:', error);
        // Fall through to set user without enrichment
      }
    }

    setArRequestedForUser(user);
    setArRequestedForQuery("");
    setArSearchResults(null);
  };

  const handleArClearUser = () => {
    setArRequestedForUser(null);
    setArRequestedForQuery("");
    setArSearchResults(null);
  };
  const fallbackLoadImpactOptions: Exclude<LoadImpact, "">[] = ["0 - 25%", "26 - 50%", "51 - 75%", "76 - 100%"];

 
  const showSystemIssueFields = draft.category === "System Issue";
  const effectiveCategoryOptions = categoryOptions && categoryOptions.length ? categoryOptions : CATEGORY_OPTIONS;
  const effectiveSubCategoryMap = subCategoryMap && Object.keys(subCategoryMap).length
    ? subCategoryMap
    : SUBCATEGORY_OPTIONS;
  // Case 2 (no existing tickets) locks subCategory via LockedValueBox above and never
  // reads this list; Case 3/4 (editable form) always get the full API-driven option set.
  const subCategoryOptions = useMemo(() => {
    return effectiveSubCategoryMap[draft.category] || [];
  }, [draft.category, effectiveSubCategoryMap]);
  const sortedTriggerFields = [...(triggerFields ?? [])].sort((a, b) => a.displayOrder - b.displayOrder);
  const impactedPeopleField = sortedTriggerFields.find(
    (field) => field.fieldName === "number_of_people_impacted"
  );
  const loadImpactField = sortedTriggerFields.find(
    (field) => field.fieldName === "percent_of_loads_impacted"
  );
  const showImpactedPeopleField = showSystemIssueFields || Boolean(impactedPeopleField);
  const showLoadImpactField = showSystemIssueFields || Boolean(loadImpactField);
  const impactedPeopleOptions = [...(impactedPeopleField?.staticOptions ?? [])]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((option) => option.value || option.label);
  const loadImpactOptions = [...(loadImpactField?.staticOptions ?? [])]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((option) => option.value || option.label);

  // onContentSizeChange is only used on native (iOS/Android) to auto-grow the
  // description textarea. On web, react-native-web fires this callback during
  // the React commit/ref-attachment phase which triggers setState → re-render
  // → commit → setState … causing the "Maximum update depth exceeded" loop.
  // On web we instead rely on a fixed minHeight set in the style.
  const isWeb = Platform.OS === "web";
  const handleDescriptionContentSizeChange = isWeb
    ? undefined
    : (event: any) => {
        const { height } = event.nativeEvent.contentSize;
        const newHeight = Math.max(120, Math.min(height, 400));
        setDescriptionHeight((prev) => (prev === newHeight ? prev : newHeight));
      };

  const getTextMultiTags = (fieldName: string): string[] => {
    const raw = triggerValues?.[fieldName] ?? "";
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const addTextMultiTag = (fieldName: string) => {
    const inputVal = (textMultiInputs[fieldName] ?? "").trim();
    if (!inputVal) return;
    const existing = getTextMultiTags(fieldName);
    if (existing.includes(inputVal)) return;
    const updated = [...existing, inputVal];
    onTriggerFieldChange(fieldName, JSON.stringify(updated));
    setTextMultiInputs((prev) => ({ ...prev, [fieldName]: "" }));
  };

  const removeTextMultiTag = (fieldName: string, tag: string) => {
    const existing = getTextMultiTags(fieldName);
    const updated = existing.filter((t) => t !== tag);
    onTriggerFieldChange(fieldName, updated.length ? JSON.stringify(updated) : "");
  };

  const getStakeholderEmails = (): string[] => {
    const raw = draft.stakeholders;
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return raw
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  };

  const addStakeholderEmail = () => {
    const inputVal = stakeholderInput.trim();
    if (!inputVal) {
      setStakeholderError(null);
      return;
    }
    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inputVal)) {
      setStakeholderError("Please enter a valid email address.");
      return;
    }
    const existing = getStakeholderEmails();
    if (existing.includes(inputVal)) {
      setStakeholderError("This stakeholder email has already been added.");
      return;
    }
    if (existing.length >= 10) {
      setStakeholderError("You can add up to 10 stakeholder emails.");
      return;
    }
    const updated = [...existing, inputVal];
    onStakeholdersChange(JSON.stringify(updated));
    setStakeholderInput("");
    setStakeholderError(null);
  };

  const removeStakeholderEmail = (email: string) => {
    const existing = getStakeholderEmails();
    const updated = existing.filter((e) => e !== email);
    onStakeholdersChange(updated.length ? JSON.stringify(updated) : "");
    setStakeholderError(null);
    setStakeholderSourceMap((prev) => {
      const key = email.trim().toLowerCase();
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // ── Secondary Stakeholders "Select Users" picker (scoped to the selected Ticket Level) ──
  const parseLevelReferenceValues = (raw: string): string[] => {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [String(parsed)].filter(Boolean);
    } catch {
      return raw.split(",").map((v) => v.trim()).filter(Boolean);
    }
  };
  const selectedLevelReferenceValues = parseLevelReferenceValues(draft.levelReference);
  const stakeholderScope = {
    regions: draft.level === "Region" ? selectedLevelReferenceValues.filter((v) => v !== "COE") : [],
    markets: draft.level === "Market" ? selectedLevelReferenceValues : [],
    locations: draft.level === "Location" ? selectedLevelReferenceValues : [],
  };
  const hasStakeholderScope =
    stakeholderScope.regions.length > 0 || stakeholderScope.markets.length > 0 || stakeholderScope.locations.length > 0;
  const [showStakeholderPicker, setShowStakeholderPicker] = useState(false);

  // Tracks which Ticket Level scope value (location/market/region name) each Select-Users-added
  // stakeholder came from, so deselecting just that value removes only its stakeholders.
  const [stakeholderSourceMap, setStakeholderSourceMap] = useState<Record<string, string[]>>({});
  const getStakeholderScopeValue = (user: TicketingLocationUser): string =>
    draft.level === "Region" ? user.region : draft.level === "Market" ? user.market : user.locationName;

  const prevLevelReferenceValuesRef = useRef<string[]>(selectedLevelReferenceValues);
  // Ref to the equipment section's imperative handle — used by validateEquipment()
  const equipmentSectionRef = useRef<import("./RepairReplaceReturnEquipmentSection").EquipmentSectionHandle>(null);
  useEffect(() => {
    const prevValues = prevLevelReferenceValuesRef.current;
    const removedValues = prevValues.filter((v) => !selectedLevelReferenceValues.includes(v));
    if (removedValues.length > 0) {
      setStakeholderSourceMap((prevMap) => {
        const nextMap: Record<string, string[]> = {};
        const emailsToRemove = new Set<string>();
        Object.entries(prevMap).forEach(([emailKey, scopes]) => {
          const remainingScopes = scopes.filter((s) => !removedValues.includes(s));
          if (remainingScopes.length === 0) {
            emailsToRemove.add(emailKey);
          } else {
            nextMap[emailKey] = remainingScopes;
          }
        });
        if (emailsToRemove.size > 0) {
          const existing = getStakeholderEmails();
          const updated = existing.filter((e) => !emailsToRemove.has(e.trim().toLowerCase()));
          onStakeholdersChange(updated.length ? JSON.stringify(updated) : "");
        }
        return nextMap;
      });
    }
    prevLevelReferenceValuesRef.current = selectedLevelReferenceValues;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.levelReference, draft.level]);

  const handleAttachStakeholders = (selectedUsers: TicketingLocationUser[]) => {
    const existing = getStakeholderEmails();
    const existingLower = new Set(existing.map((e) => e.toLowerCase()));
    const toAdd: string[] = [];
    const newScopeEntries: Record<string, string> = {};
    selectedUsers.forEach((u) => {
      const email = u.email.trim();
      const key = email.toLowerCase();
      if (email && !existingLower.has(key)) {
        existingLower.add(key);
        toAdd.push(email);
        newScopeEntries[key] = getStakeholderScopeValue(u);
      }
    });
    if (!toAdd.length) return;
    const merged = [...existing, ...toAdd].slice(0, 10);
    onStakeholdersChange(JSON.stringify(merged));
    setStakeholderError(null);
    setStakeholderSourceMap((prev) => {
      const next = { ...prev };
      Object.entries(newScopeEntries).forEach(([key, scopeValue]) => {
        if (!scopeValue) return;
        const existingScopes = next[key] ?? [];
        next[key] = existingScopes.includes(scopeValue) ? existingScopes : [...existingScopes, scopeValue];
      });
      return next;
    });
  };

  // Users often type an email and hit Submit without pressing Enter/tapping the add icon first;
  // commit that pending text here so it isn't silently dropped from the submitted stakeholderEmails.
  useImperativeHandle(ref, () => ({
    flushStakeholderInput: () => {
      const existing = getStakeholderEmails();
      const inputVal = stakeholderInput.trim();
      if (!inputVal) {
        return existing;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(inputVal) || existing.includes(inputVal) || existing.length >= 10) {
        // Leave invalid/duplicate/over-limit text in the box — existing field-level validation will surface it.
        return existing;
      }
      const updated = [...existing, inputVal];
      onStakeholdersChange(JSON.stringify(updated));
      setStakeholderInput("");
      setStakeholderError(null);
      return updated;
    },
    validateEquipment: () => {
      return equipmentSectionRef.current?.validate() ?? true;
    },
  }), [stakeholderInput, draft.stakeholders, onStakeholdersChange]);

  const handleLoadImpactChange = (value: string) => {
    const optionsToValidate = (loadImpactOptions.length
      ? loadImpactOptions
      : fallbackLoadImpactOptions) as Exclude<LoadImpact, "">[];
    if (optionsToValidate.includes(value as Exclude<LoadImpact, "">)) {
      onLoadImpactChange(value as Exclude<LoadImpact, "">);
    }
  };

  const urgencyDotStyleMap: Record<UrgencyLevel, any> = {
    Critical: styles.urgencyCritical,
    High: styles.urgencyHigh,
    Medium: styles.urgencyMedium,
    Low: styles.urgencyLow,
  };

  const isSubmitActionDisabled = isSubmitDisabled || isSubmitting;

  // Dark-mode style overrides for mobile form elements
  const dk = isDark;
  const dkCard = dk ? { backgroundColor: colors.background.surface } : null;
  const dkPanel = dk ? { backgroundColor: colors.background.elevated, borderColor: colors.border.default } : null;
  const dkLabel = dk ? { color: colors.text.secondary } : null;
  const dkHeaderTitle = dk ? { color: colors.text.primary } : null;
  const dkHeaderSubtitle = dk ? { color: colors.text.secondary } : null;
  const dkInput = dk ? { backgroundColor: colors.background.elevated, borderColor: colors.border.default, color: colors.text.primary } : null;
  const dkInputDisabled = dk ? { backgroundColor: colors.background.surface, borderColor: colors.border.default, color: colors.text.disabled } : null;
  const dkHelper = dk ? { color: colors.text.disabled } : null;
  const dkChip = dk ? { backgroundColor: colors.background.elevated, borderColor: colors.border.default } : null;
  const dkChipActive = dk ? { backgroundColor: colors.background.surface, borderColor: colors.brand.primary } : null;
  const dkChipText = dk ? { color: colors.text.primary } : null;
  const dkChipTextActive = dk ? { color: colors.brand.primary } : null;
  const dkTextarea = dk ? { backgroundColor: colors.background.elevated, borderColor: colors.border.default, color: colors.text.primary } : null;
  const placeholderColor = dk ? colors.text.disabled : "#9aa4b2";

  return (
    <View style={[mobile ? mobileStyles.formCard : styles.panel, mobile ? dkCard : dkPanel]}>
      {!mobile ? (
        <View style={styles.formCardHeader}>
          <Text style={[styles.formHeaderTitle, dkHeaderTitle]}>{isAccessRequest ? "Access Request Details" : "Ticket Details"}</Text>
          <Text style={[styles.formHeaderSubtitle, dkHeaderSubtitle]}>All fields marked with * are required</Text>
        </View>
      ) : null}

      <View style={styles.formGrid}>
        <View style={styles.fieldFull}>
          <Text style={[styles.label, dkLabel]}>Submitter <Text style={styles.required}>*</Text></Text>
          <TextInput
            style={[styles.input, styles.inputFull, !isLimitedAccess ? styles.inputDisabled : null, !isLimitedAccess ? dkInputDisabled : dkInput]}
            value={submitter ?? draft.submitter}
            onChangeText={onSubmitterChange}
            editable={isLimitedAccess && !lockedFields.submitter}
            maxLength={100}
            placeholder="Your email address"
            placeholderTextColor={placeholderColor}
            keyboardType="email-address"
            autoCapitalize="none"
            onBlur={() => onFieldBlur?.('submitter')}
          />
          {!isLimitedAccess ? (
            <Text style={[styles.helperText, dkHelper]}>Pre-populated from your logged-in account.</Text>
          ) : (
            <Text style={[styles.helperText, dkHelper]}>Enter your email address ({(submitter ?? draft.submitter).length}/100).</Text>
          )}
          {fieldErrors.submitterEmail ? <Text style={[styles.helperText, { color: "#c12c01" }]}>{fieldErrors.submitterEmail}</Text> : null}
        </View>

        <View style={styles.fieldFull}>
          <Text style={[styles.label, dkLabel]}>Title <Text style={styles.required}>*</Text></Text>
          {lockedFields.title ? (
            <LockedValueBox value={draft.title} />
          ) : (
            <TextInput
              style={[styles.input, styles.inputFull, dkInput]}
              value={draft.title}
              onChangeText={onTitleChange}
              onBlur={() => onFieldBlur?.('title')}
              maxLength={150}
              placeholder="Short, descriptive summary of the issue"
              placeholderTextColor={placeholderColor}
            />
          )}
          {lockedFields.title ? (
            <Text style={[styles.helperText, dkHelper]}>Set by the system for a new user access request. Not editable.</Text>
          ) : (
            <View style={styles.fieldMetaRow}>
              <Text style={[styles.helperText, dkHelper]}>Keep it descriptive so the Sustain Team can triage quickly.</Text>
              <Text style={[styles.helperText, dkHelper, draft.title.length >= 150 ? { color: "#c12c01", fontWeight: "600" } : null]}>{draft.title.length}/150</Text>
            </View>
          )}
        </View>

        {lockedFields.category ? (
          <View style={styles.fieldGroup}>
            <Text style={[styles.label, dkLabel]}>Category <Text style={styles.required}>*</Text></Text>
            <LockedValueBox value={draft.category} />
            <Text style={[styles.helperText, dkHelper]}>Set by the system for a new user access request. Not editable.</Text>
          </View>
        ) : (
          <SelectField
            id="category"
            label="Category"
            value={draft.category}
            options={effectiveCategoryOptions}
            required
            placeholder="Select a category..."
            errorText={fieldErrors.category}
            activeDropdownId={mobile ? activeMobileDropdownId : undefined}
            onDropdownToggle={mobile ? setActiveMobileDropdownId : undefined}
            onBlur={() => onFieldBlur?.("category")}
            onChange={onCategoryChange}
          />
        )}

        {lockedFields.subCategory ? (
          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Sub-Category <Text style={styles.required}>*</Text></Text>
            <LockedValueBox value={draft.subCategory} />
            <Text style={styles.helperText}>Set to "New Access" for a new user access request. Not editable.</Text>
          </View>
        ) : subCategoryOptions.length > 0 ? (
          <SelectField
            id="subCategory"
            label="Sub-Category"
            value={draft.subCategory}
            options={subCategoryOptions}
            required={subCategoryOptions.length > 0}
            placeholder={draft.category ? "Select a sub-category..." : "Select a category first"}
            errorText={fieldErrors.subCategory}
            activeDropdownId={mobile ? activeMobileDropdownId : undefined}
            onDropdownToggle={mobile ? setActiveMobileDropdownId : undefined}
            onBlur={() => onFieldBlur?.("subCategory")}
            onChange={onSubCategoryChange}
          />
        ) : null}

        {!lockedFields.subCategory && draft.subCategory && tertiaryCategoryOptions.length > 0 ? (
          <SelectField
            id="tertiaryCategory"
            label="Tertiary Category"
            value={draft.tertiaryCategory}
            options={tertiaryCategoryOptions}
            required
            placeholder="Select a tertiary category..."
            errorText={fieldErrors.tertiaryCategory}
            activeDropdownId={mobile ? activeMobileDropdownId : undefined}
            onDropdownToggle={mobile ? setActiveMobileDropdownId : undefined}
            onBlur={() => onFieldBlur?.("tertiaryCategory")}
            onChange={onTertiaryCategoryChange}
          />
        ) : null}

        {/* ══════════════════════════════════════════════════════════════════
            REPAIR / REPLACE / RETURN GUIDE — shown only when category =
            "Repair / Replace / Return", helps the submitter pick the right
            sub-category before the rest of the normal ticket fields render.
            ══════════════════════════════════════════════════════════════════ */}
        {isRepairReplaceReturn ? (
          <View style={styles.fieldFull}>
            <RepairReplaceReturnGuide subCategory={draft.subCategory} onSelect={onSubCategoryChange} />
            <RepairReplaceReturnEquipmentSection ref={equipmentSectionRef} />
          </View>
        ) : null}

        {/* ══════════════════════════════════════════════════════════════════
            ACCESS REQUEST FORM — shown only when category = "Access Request"
            ══════════════════════════════════════════════════════════════════ */}
        {isAccessRequest ? (
          <View style={[styles.fieldFull, { marginTop: 4, ...Platform.select({ web: { overflow: "visible" } as any, default: {} }) }]}>

            {fieldErrors.accessRequest ? (
              <View style={[arStyle.tempAccessBanner, { backgroundColor: "#fff1f2", borderColor: "#fecdd3", marginBottom: 16 }]}>
                <MaterialIcons name="error-outline" size={16} color="#c12c01" style={{ marginTop: 2, flexShrink: 0 }} />
                <Text style={[arStyle.tempAccessBannerText, { color: "#9f1239" }]}>
                  {fieldErrors.accessRequest}
                </Text>
              </View>
            ) : null}

            {/* ── Temporary Access warning banner ── */}
            {isTemporaryAccess ? (
              <View style={arStyle.tempAccessBanner}>
                <MaterialIcons name="schedule" size={16} color="#b45309" style={{ marginTop: 2, flexShrink: 0 }} />
                <Text style={arStyle.tempAccessBannerText}>
                  <Text style={{ fontWeight: "700" }}>Temporary access</Text> is reviewed by the Sustain Team like any other ticket and{" "}
                  <Text style={{ fontWeight: "700" }}>reverts automatically on the end date</Text> — no follow-up ticket needed. Limited to 14 days at a time.
                </Text>
              </View>
            ) : null}

            {/* ── Requested For ── */}
            <View style={[styles.fieldFull, { marginBottom: 16, zIndex: 60, ...Platform.select({ web: { overflow: "visible" } as any, default: {} }) }]}>
              <Text style={styles.label}>Requested For <Text style={styles.required}>*</Text></Text>
              {lockedFields.submitter ? (
                <>
                  <LockedValueBox value={arRequestedForUser?.name || draft.submitter || ""} />
                  <Text style={styles.helperText}>Set to the signed-in user for a new access request. Not editable.</Text>
                </>
              ) : (
                <>
                  {/* Search input */}
                  <View>
                    <TextInput
                      style={[styles.input, styles.inputFull]}
                      value={arRequestedForQuery}
                      onChangeText={handleArSearch}
                      placeholder="Search by name, GPID, or email (Entra ID people picker)"
                      placeholderTextColor="#9aa4b2"
                      autoCapitalize="none"
                      autoCorrect={false}
                    />
                    {/* Search results / no-match block */}
                    {arRequestedForQuery.trim().length > 0 ? (
                      arSearchLoading ? (
                        <View style={arStyle.searchBlock}>
                          <View style={arStyle.noMatchBox}>
                            <Text style={arStyle.noMatchText}>Searching...</Text>
                          </View>
                        </View>
                      ) : arSearchResults && arSearchResults.length === 0 ? (
                        <View style={arStyle.searchBlock}>
                          <View style={arStyle.noMatchBox}>
                            <Text style={arStyle.noMatchText}>No match.</Text>
                          </View>
                        </View>
                      ) : arSearchResults && arSearchResults.length > 0 ? (
                        <View style={arStyle.searchBlock}>
                          {arSearchResults.map((u, index) => (
                            <Pressable
                              key={u.gpid || `${u.email}-${index}`}
                              style={({ pressed }) => [
                                arStyle.searchResultRow,
                                index === arSearchResults.length - 1 ? arStyle.searchResultRowLast : null,
                                pressed && { backgroundColor: "#f3f7fd" },
                              ]}
                              onPress={() => handleArSelectUser(u)}
                            >
                              <View style={arStyle.avatarCircle}>
                                <Text style={arStyle.avatarText}>{getInitials(u.name)}</Text>
                              </View>
                              <View style={{ flex: 1 }}>
                                <Text style={arStyle.resultName}>{u.name}</Text>
                                <Text style={arStyle.resultMeta}>{formatArUserMeta(u)}</Text>
                              </View>
                            </Pressable>
                          ))}
                        </View>
                      ) : null
                    ) : null}
                  </View>

                  {/* Selected user card */}
                  {arRequestedForUser ? (
                    <View style={arStyle.userCard}>
                      <View style={arStyle.avatarCircle}>
                        <Text style={arStyle.avatarText}>{getInitials(arRequestedForUser.name)}</Text>
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={arStyle.cardName}>{arRequestedForUser.name}</Text>
                        <Text style={arStyle.cardMeta}>{formatArUserMeta(arRequestedForUser)}</Text>
                      </View>
                      <Pressable
                        onPress={handleArClearUser}
                        style={arStyle.cardClose}
                        hitSlop={6}
                      >
                        <Text style={arStyle.cardCloseText}>×</Text>
                      </Pressable>
                    </View>
                  ) : null}

                  <Text style={styles.helperText}>Defaults to you. Overwrite to request on behalf of another PepsiCo user.</Text>
                </>
              )}
            </View>

            {/* ── Normal User: additional fields ── */}
            <>
              {/* Modify Access / Temporary Access: Current Role (read-only) */}
              {(isModifyAccess || isTemporaryAccess) ? (
                <View style={[styles.fieldGroup, { marginTop: 8 }]}>
                  <Text style={styles.label}>Current Role</Text>
                  <TextInput
                    style={[styles.input, styles.inputDisabled]}
                    value={arCurrentRole}
                    editable={false}
                  />
                </View>
              ) : null}

              {/* Access Type Tier (story 367227/317066) — Support roles skip Organization/Department entirely. Not applicable to Temporary Access. */}
              {!isTemporaryAccess ? (
                <View style={[styles.fieldGroup, { marginTop: 8 }]}>
                  <SelectField
                    id="ar-access-type-tier"
                    label="Access Type Tier"
                    value={arAccessTypeTier}
                    options={[...ACCESS_TYPE_TIERS]}
                    required
                    placeholder="Select access type tier..."
                    activeDropdownId={mobile ? arDropdownOpen : undefined}
                    onDropdownToggle={mobile ? setArDropdownOpen : undefined}
                    onChange={(v) => {
                      const tier = v as typeof ACCESS_TYPE_TIERS[number];
                      setArAccessTypeTier(tier);
                      setArRole("");
                      setArSelectedLocations([]);
                      setArPendingPrePopulate(null);
                      if (tier === 'Support') {
                        setArOrganization("");
                        setArDepartment("");
                      }
                    }}
                    helperText="Field and 3PL/Outside Labor use Organization, Department, and Role below. Support roles get access to everything."
                  />
                </View>
              ) : null}

              {/* Organization + Department row */}
              {arAccessTypeTier !== 'Support' ? (
              <View style={{ flexDirection: "row", flexWrap: "wrap", columnGap: 24, rowGap: 0, marginTop: 4, ...Platform.select({ web: { overflow: "visible", zIndex: 40, position: "relative" } as any, default: {} }) }}>
                <SelectField
                  id="ar-organization"
                  label="Organization"
                  value={arOrganization}
                  options={[...AR_ORGANIZATIONS]}
                  required
                  placeholder="Select organization..."
                  activeDropdownId={mobile ? arDropdownOpen : undefined}
                  onDropdownToggle={mobile ? setArDropdownOpen : undefined}
                  onChange={(v) => {
                    setArOrganization(v);
                    setArDepartment("");
                    setArRole("");
                    setArSelectedLocations([]);
                    setArPendingPrePopulate(null);
                  }}
                  helperText="Scopes which locations are available downstream."
                />

                <SelectField
                  id="ar-department"
                  label="Department"
                  value={arDepartment}
                  options={arDepartmentOptions}
                  required
                  placeholder={arOrganization ? "Select a department..." : "Select an organization first"}
                  disabled={!arOrganization}
                  activeDropdownId={mobile ? arDropdownOpen : undefined}
                  onDropdownToggle={mobile ? setArDropdownOpen : undefined}
                  onChange={(v) => {
                    setArDepartment(v);
                    setArRole("");
                    setArSelectedLocations([]);
                    setArPendingPrePopulate(null);
                  }}
                  helperText="Determines the available roles."
                />
              </View>
              ) : null}

              {/* Role / Temporary Role + Access End Date */}
              {isTemporaryAccess ? (
                /* Temporary Access: Temporary Role + Access End Date side by side */
                <View style={{ flexDirection: "row", flexWrap: "wrap", columnGap: 24, rowGap: 0, marginTop: 4, ...Platform.select({ web: { overflow: "visible", zIndex: 30, position: "relative" } as any, default: {} }) }}>
                  <View style={{ width: Platform.OS === "web" ? 420 : "100%", marginBottom: 16 }}>
                    <SelectField
                      id="ar-role"
                      label="Temporary Role"
                      value={arRole}
                      options={arRoleOptions}
                      required
                      placeholder={
                        arAccessTypeTier === 'Support'
                          ? (arSupportTierRolesLoading ? "Loading roles..." : "Select role...")
                          : (arDepartment ? "Select a role..." : "Select a department first")
                      }
                      disabled={arAccessTypeTier === 'Support' ? false : !arDepartment}
                      activeDropdownId={mobile ? arDropdownOpen : undefined}
                      onDropdownToggle={mobile ? setArDropdownOpen : undefined}
                      onChange={(v) => {
                        setArRole(v);
                        setArSelectedLocations([]);
                        setArPendingPrePopulate(null);
                      }}
                    />
                    {roleScope ? (
                      <Text style={styles.helperText}>
                        Role scope: {" "}
                        <Text style={{ color: roleScope.accessColor, fontWeight: "700" }}>
                          {roleScope.accessLabel}
                        </Text>
                        {" — "}
                        {roleScope.scopeLabel}
                      </Text>
                    ) : null}
                  </View>
                  <View style={{ width: Platform.OS === "web" ? 420 : "100%", marginBottom: 16 }}>
                    <Text style={styles.label}>
                      Access End Date <Text style={styles.required}>*</Text>
                    </Text>
                    <LhDatePicker
                      value={arAccessEndDate}
                      onChange={setArAccessEndDate}
                      placeholder="Select end date"
                      minimumDate={new Date()}
                      maximumDate={arTempMaxDate}
                      error={false}
                      colorScheme={isDark ? "dark" : "light"}
                      iconColor={isDark ? "#FFFFFF" : "#3A3A3A"}
                    />
                    <Text style={styles.helperText}>
                      Temporary access is limited to 14 days and reverts automatically on the end date.
                    </Text>
                  </View>
                </View>
              ) : (
                /* New Access / Modify Access: regular Role field */
                <View style={{ marginTop: 4, ...Platform.select({ web: { overflow: "visible", zIndex: 30, position: "relative" } as any, default: {} }) }}>
                  <SelectField
                    id="ar-role"
                    label={isModifyAccess ? "New Role" : "Role"}
                    value={arRole}
                    options={arRoleOptions}
                    required
                    placeholder={
                      arAccessTypeTier === 'Support'
                        ? (arSupportTierRolesLoading ? "Loading roles..." : "Select role...")
                        : (arDepartment ? "Select a role..." : "Select a department first")
                    }
                    disabled={arAccessTypeTier === 'Support' ? false : !arDepartment}
                    activeDropdownId={mobile ? arDropdownOpen : undefined}
                    onDropdownToggle={mobile ? setArDropdownOpen : undefined}
                    onChange={(v) => {
                      setArRole(v);
                      setArSelectedLocations([]);
                      setArPendingPrePopulate(null);
                    }}
                  />
                  {/* Role scope hint */}
                  {roleScope ? (
                    <Text style={styles.helperText}>
                      Role scope: {" "}
                      <Text style={{ color: roleScope.accessColor, fontWeight: "700" }}>
                        {roleScope.accessLabel}
                      </Text>
                      {" — "}
                      {roleScope.scopeLabel}
                    </Text>
                  ) : null}
                </View>
              )}

              {/* Location picker */}
              {showLocationPicker ? (
                <View style={{ marginTop: 12 }}>
                  {arLocationsLoading ? (
                    <View style={{ paddingVertical: 12, alignItems: "center" }}>
                      <Text style={[styles.helperText, { color: "#005cbc" }]}>Loading locations…</Text>
                    </View>
                  ) : (
                    <AccessRequestLocationPicker
                      pickerType={
                        isModifyAccess && !roleScope
                          ? "site"
                          : roleScope?.type === "full"
                          ? "site"
                          : (roleScope?.type as LocationPickerType ?? "site")
                      }
                      organization={arOrganization}
                      selectedItems={displaySelectedLocations}
                      onSelectionChange={setArSelectedLocations}
                      // Always pass the activeLocationTree (can be an empty array)
                      // so the picker can show the explicit "no locations" banner
                      // when the API returned zero results.
                      locationTree={activeLocationTree}
                      selectionLocked={roleScope?.type === "full"}
                      maxRegionSelections={getMaxRegionSelectionsForRole(arRole)}
                    />
                  )}
                </View>
              ) : null}

              {/* ── Accessible Modules Card — shown for all Access Request subtypes ── */}
              {isAccessRequest ? (
                <View style={arStyle.modulesCard}>
                  <View style={arStyle.modulesCardHeader}>
                    <MaterialIcons name="info-outline" size={16} color="#1d4ed8" />
                    <View style={{ flex: 1, marginLeft: 8 }}>
                      <Text style={arStyle.modulesCardTitle}>What does this access provide?</Text>
                      <Text style={arStyle.modulesCardSubtitle}>
                        From the Config module's role privileges for the selected site(s)
                      </Text>
                    </View>
                  </View>
                  <View style={arStyle.modulesSameAccessBadge}>
                    <Text style={arStyle.modulesSameAccessBadgeText}>
                      {roleScope?.type === "full" ? "DEFAULT CONFIGURATION" : "SAME ACCESS AT ALL SELECTED SITES"}
                    </Text>
                  </View>
                  <Text style={arStyle.modulesAccessLabel}>
                    {roleScope?.type === "full" ? (
                      `Based on the default ${arRole || "role"} configuration`
                    ) : (
                      <>With this role, the user will have <Text style={{ fontWeight: "700" }}>EDIT</Text> access to:</>
                    )}
                  </Text>
                  <View style={arStyle.modulesPillsRow}>
                    {accessibleModulesLoading ? (
                      STATIC_ACCESSIBLE_MODULES.map((mod) => (
                        <View key={mod} style={[arStyle.modulePill, arStyle.modulePillLoading]}>
                          <View style={arStyle.modulePillSkeleton} />
                        </View>
                      ))
                    ) : fullAccessModules.length > 0 ? (
                      fullAccessModules.map((module) => (
                        <View key={module.accessId} style={arStyle.moduleAccessItem}>
                          <Text style={arStyle.moduleAccessItemText}>{module.moduleName}</Text>
                        </View>
                      ))
                    ) : visibleAccessibleModules.length === 0 ? (
                      <Text style={arStyle.modulesAccessLabel}>Select a role to view accessible modules.</Text>
                    ) : null}
                  </View>
                  {!accessibleModulesLoading && viewOnlyModules.length > 0 ? (
                    <>
                      <Text style={[arStyle.modulesAccessLabel, arStyle.viewOnlyLabel]}>
                        With this role, the user will have <Text style={{ fontWeight: "700" }}>VIEW ONLY</Text> access to:
                      </Text>
                      <View style={arStyle.modulesPillsRow}>
                        {viewOnlyModules.map((module) => (
                          <View key={module.accessId} style={arStyle.moduleAccessItem}>
                            <Text style={arStyle.moduleAccessItemText}>{module.moduleName}</Text>
                          </View>
                        ))}
                      </View>
                    </>
                  ) : null}
                  {isTemporaryAccess && arAccessEndDate ? (
                    <Text style={arStyle.modulesEndDateNote}>
                      Temporary access ends {" "}
                      <Text style={{ fontWeight: "700" }}>
                        {arAccessEndDate.toLocaleDateString("en-US", {
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                        })}
                      </Text>
                      , when access reverts automatically to the user's current role.
                    </Text>
                  ) : null}
                </View>
              ) : null}
            </>

            {/* ── Justification (always shown for Access Request) ── */}
            <View style={[styles.fieldFull, { marginTop: 12, ...Platform.select({ web: { zIndex: 10, position: "relative" } as any, default: {} }) }]}>
              <Text style={styles.label}>
                Justification <Text style={styles.optional}>optional</Text>
              </Text>
              <TextInput
                style={[styles.input, styles.inputFull, styles.textarea, { height: 100 }]}
                value={arJustification}
                onChangeText={setArJustification}
                multiline
                placeholder="Optional business context. Required only when the Sustain Team denies."
                placeholderTextColor="#9aa4b2"
              />
              <Text style={styles.helperText}>
                No mandatory justification on submit — Sustain supplies a reason on Deny or Modify.
              </Text>
            </View>
          </View>
        ) : null}

        {/* ══════════════════════════════════════════════════════════════════
            NORMAL TICKET FIELDS — hidden when Access Request is selected
            ══════════════════════════════════════════════════════════════════ */}
        {!isAccessRequest ? (
          <>
          {showImpactedPeopleField ? (
            <SelectField
              id="impactedPeople"
              label={impactedPeopleField?.fieldLabel || "Number of People Impacted"}
              value={draft.impactedPeople}
              options={impactedPeopleOptions}
              required={impactedPeopleField?.required ?? true}
              placeholder={impactedPeopleField?.placeholder || "Select..."}
              disabled={!impactedPeopleOptions.length}
              activeDropdownId={mobile ? activeMobileDropdownId : undefined}
              onDropdownToggle={mobile ? setActiveMobileDropdownId : undefined}
              onChange={onImpactedPeopleChange}
            />
          ) : null}

          {showLoadImpactField ? (
          <SelectField
            id="loadImpact"
            label={loadImpactField?.fieldLabel || "Percent of Loads Impacted"}
            value={draft.loadImpact}
            options={loadImpactOptions.length ? loadImpactOptions : fallbackLoadImpactOptions}
            required={loadImpactField?.required ?? true}
            placeholder={loadImpactField?.placeholder || "Select..."}
            activeDropdownId={mobile ? activeMobileDropdownId : undefined}
            onDropdownToggle={mobile ? setActiveMobileDropdownId : undefined}
            onChange={handleLoadImpactChange}
          />
        ) : null}

        {showSystemIssueFields ? (
          <View style={styles.fieldFull}>
            <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "flex-start", paddingHorizontal: 0 }}>
              <Text style={[styles.label, { marginRight: 12, flexShrink: 1, marginBottom: 0 }]}>This is stopping my ability to Make, Move, Sell</Text>
              <Pressable
                onPress={() => onMmsToggleChange(!draft.mmsToggle)}
                style={[
                  styles.customToggleTrack,
                  draft.mmsToggle ? styles.customToggleTrackOn : styles.customToggleTrackOff,
                  // Inline colours are not rewritten by the Dark stylesheet sweep. The swept
                  // off-track and white thumb both collapse to the card colour and vanish.
                  isDark && { backgroundColor: draft.mmsToggle ? "#3D8BFF" : "#5A5A5A" },
                ]}
                accessibilityRole="switch"
                accessibilityState={{ checked: draft.mmsToggle }}
              >
                {draft.mmsToggle ? (
                  <>
                    <Text style={[styles.customToggleLabel, styles.customToggleLabelOn]}>YES</Text>
                    <View style={[styles.customToggleThumb, isDark && { backgroundColor: "#FFFFFF" }]} />
                  </>
                ) : (
                  <>
                    <View style={[styles.customToggleThumb, isDark && { backgroundColor: "#FFFFFF" }]} />
                    <Text style={[styles.customToggleLabel, styles.customToggleLabelOff]}>NO</Text>
                  </>
                )}
              </Pressable>
            </View>
            <Text style={styles.helperText}>Toggle on if this issue is blocking Make, Move, or Sell functionality.</Text>
          </View>
        ) : null}

        {sortedTriggerFields
          .filter(
            (field) =>
              field.fieldName !== "category" &&
              field.fieldName !== "sub_category" &&
              field.fieldName !== "title" &&
              field.fieldName !== "issue_description" &&
              field.fieldName !== "secondary_stakeholders" &&
              field.fieldName !== "number_of_people_impacted" &&
              field.fieldName !== "percent_of_loads_impacted" &&
              field.fieldName !== "urgency" &&
              field.fieldName !== "ticket_level" &&
              field.fieldName !== "level_reference" &&
              field.fieldName !== "attachments"
          )
          .map((field) => {
            const fieldValue = triggerValues?.[field.fieldName] ?? "";
            const staticOptions = [...(field.staticOptions ?? [])]
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map((option) => option.value || option.label);
            const asyncOptions = triggerFieldOptions?.[field.fieldName] ?? [];

            // from_location / to_location: use location names from Redux appData
            const isLocationField =
              field.dropdownSource === "navigator_location_api";
            const locationDerivedOptions = isLocationField ? locationNames : [];

            // navigator_module: use module names from Redux appData.moduleAccess
            const isNavigatorModuleField = field.fieldName === "navigator_module";
            const moduleDerivedOptions = isNavigatorModuleField ? moduleOptions : [];

            const selectOptions = isLocationField
              ? locationDerivedOptions
              : isNavigatorModuleField && moduleDerivedOptions.length > 0
              ? moduleDerivedOptions
              : isNavigatorModuleField && staticOptions.length === 0 && asyncOptions.length === 0
              ? NAVIGATOR_MODULE_FALLBACK_OPTIONS
              : staticOptions.length
              ? staticOptions
              : asyncOptions;
            const hasSelectableOptions = selectOptions.length > 0;

            if (
              field.fieldType === "single_select" ||
              field.fieldType === "single_select_searchable"
            ) {
              return (
                <SelectField
                  key={field.fieldName}
                  id={`trigger-${field.fieldName}`}
                  label={field.fieldLabel}
                  value={fieldValue}
                  options={selectOptions}
                  required={field.required}
                  placeholder={field.placeholder || "Select..."}
                  disabled={!hasSelectableOptions}
                  errorText={fieldErrors[field.fieldName]}
                  searchable={field.fieldType === "single_select_searchable"}
                  activeDropdownId={mobile ? activeMobileDropdownId : undefined}
                  onDropdownToggle={mobile ? setActiveMobileDropdownId : undefined}
                  onBlur={() => onFieldBlur?.(`trigger_${field.fieldName}`)}
                  onChange={(value) => onTriggerFieldChange(field.fieldName, value)}
                />
              );
            }

            if (
              field.fieldType === "multi_select" ||
              field.fieldType === "multi_select_searchable"
            ) {
              return (
                <MultiSelectFieldTemplate
                  key={field.fieldName}
                  id={`trigger-${field.fieldName}`}
                  label={field.fieldLabel}
                  value={fieldValue}
                  options={selectOptions}
                  required={field.required}
                  placeholder={field.placeholder || "Select..."}
                  disabled={!hasSelectableOptions}
                  errorText={fieldErrors[field.fieldName]}
                  searchable={field.fieldType === "multi_select_searchable"}
                  activeDropdownId={mobile ? activeMobileDropdownId : undefined}
                  onDropdownToggle={mobile ? setActiveMobileDropdownId : undefined}
                  onBlur={() => onFieldBlur?.(`trigger_${field.fieldName}`)}
                  onChange={(value) => onTriggerFieldChange(field.fieldName, value)}
                />
              );
            }

            if (field.fieldType === "text_multi") {
              const tags = getTextMultiTags(field.fieldName);
              const currentInput = textMultiInputs[field.fieldName] ?? "";
              return (
                <View style={styles.fieldGroup} key={field.fieldName}>
                  <Text style={styles.label}>
                    {field.fieldLabel} {field.required ? <Text style={styles.required}>*</Text> : null}
                  </Text>
                  <View style={[styles.input, { flexDirection: "row", alignItems: "center", paddingVertical: 0, paddingHorizontal: 0 }]}>
                    <TextInput
                      style={[
                        styles.input,
                        {
                          flex: 1,
                          borderWidth: 0,
                          marginBottom: 0,
                          paddingRight: 36,
                        },
                      ]}
                      value={currentInput}
                      onChangeText={(value) =>
                        setTextMultiInputs((prev) => ({ ...prev, [field.fieldName]: value }))
                      }
                      onSubmitEditing={() => addTextMultiTag(field.fieldName)}
                      blurOnSubmit={false}
                      placeholder={field.placeholder || "Enter value and press Enter"}
                      placeholderTextColor="#9aa4b2"
                      returnKeyType="done"
                    />
                    <Pressable
                      onPress={() => addTextMultiTag(field.fieldName)}
                      style={({ pressed }: { pressed: boolean }) => ([
                        {
                          position: "absolute",
                          right: 8,
                          top: 0,
                          bottom: 0,
                          justifyContent: "center" as const,
                          alignItems: "center" as const,
                          paddingHorizontal: 6,
                          opacity: pressed ? 0.6 : 1,
                        },
                      ])}
                    >
                      <Text style={{ fontSize: 18, color: currentInput.trim() ? "#006fce" : "#9aa4b2", fontWeight: "600" }}>↵</Text>
                    </Pressable>
                  </View>
                  {tags.length > 0 && (
                    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                      {tags.map((tag) => (
                        <View
                          key={tag}
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            backgroundColor: "#e8f0fe",
                            borderRadius: 16,
                            paddingVertical: 4,
                            paddingLeft: 10,
                            paddingRight: 6,
                            borderWidth: 1,
                            borderColor: "#006fce",
                          }}
                        >
                          <Text style={{ fontSize: 13, color: "#006fce", marginRight: 4 }}>{tag}</Text>
                          <Pressable
                            onPress={() => removeTextMultiTag(field.fieldName, tag)}
                            style={({ pressed }: { pressed: boolean }) => ([
                              {
                                width: 18,
                                height: 18,
                                borderRadius: 9,
                                backgroundColor: pressed ? "#006fce" : "#c5d9f5",
                                justifyContent: "center" as const,
                                alignItems: "center" as const,
                              },
                            ])}
                          >
                            <Text style={{ fontSize: 12, color: "#006fce", lineHeight: 18, textAlign: "center" }}>×</Text>
                          </Pressable>
                        </View>
                      ))}
                    </View>
                  )}
                  {field.tooltip ? <Text style={styles.helperText}>{field.tooltip}</Text> : null}
                </View>
              );
            }

            // GAP-05: URL fields get onBlur validation and https:// enforcement
            if (field.fieldType === "text_url") {
              return (
                <View style={styles.fieldGroup} key={field.fieldName}>
                  <Text style={styles.label}>
                    {field.fieldLabel} {field.required ? <Text style={styles.required}>*</Text> : null}
                  </Text>
                  <TextInput
                    style={[styles.input, fieldErrors[field.fieldName] ? { borderColor: "#c12c01" } : null]}
                    value={fieldValue}
                    onChangeText={(value) => onTriggerFieldChange(field.fieldName, value)}
                    onBlur={() => onFieldBlur?.(`trigger_${field.fieldName}`)}
                    maxLength={field.charLimit ?? 500}
                    placeholder={field.placeholder || "https://"}
                    placeholderTextColor="#9aa4b2"
                    keyboardType="url"
                    autoCapitalize="none"
                  />
                  {fieldErrors[field.fieldName] ? (
                    <Text style={[styles.helperText, { color: "#c12c01" }]}>{fieldErrors[field.fieldName]}</Text>
                  ) : null}
                  {field.tooltip ? <Text style={styles.helperText}>{field.tooltip}</Text> : null}
                </View>
              );
            }

            return (
              <View style={styles.fieldGroup} key={field.fieldName}>
                <Text style={styles.label}>
                  {field.fieldLabel} {field.required ? <Text style={styles.required}>*</Text> : null}
                </Text>
                <TextInput
                  style={styles.input}
                  value={fieldValue}
                  onChangeText={(value) => onTriggerFieldChange(field.fieldName, value)}
                  maxLength={field.charLimit ?? undefined}
                  placeholder={field.placeholder || "Enter value"}
                  placeholderTextColor="#9aa4b2"
                  onBlur={() => onFieldBlur?.(`trigger_${field.fieldName}`)}
                />
                {field.tooltip ? <Text style={styles.helperText}>{field.tooltip}</Text> : null}
                {fieldErrors[field.fieldName] ? (
                  <Text style={[styles.helperText, { color: "#c12c01" }]}>{fieldErrors[field.fieldName]}</Text>
                ) : null}
              </View>
            );
          })}

        <View style={styles.fieldFull}>
          <Text style={[styles.label, dkLabel]}>Ticket Level <Text style={styles.required}>*</Text></Text>
          <View style={styles.chipRow}>
            {(isRepairReplaceReturn
              ? (["Location"] as const)
              : (["Location", "Market", "Region"] as const)
            ).map((item) => {
              const active = draft.level === item;
              return (
                <Pressable
                  key={item}
                  onPress={() => {
                    onLevelChange(item);
                    // Reset levelReference when changing level
                    onLevelReferenceChange("");
                    setRegionLimitMessage(null);
                    onFieldBlur?.("level");
                  }}
                  style={(state: any) => [
                    styles.chip,
                    active ? styles.chipActive : null,
                    dk && !active ? dkChip : null,
                    dk && active ? dkChipActive : null,
                    state.pressed ? styles.chipPressed : null,
                  ]}
                >
                  <Text style={[styles.chipText, active ? styles.chipTextActive : null, dk && !active ? dkChipText : null, dk && active ? dkChipTextActive : null]}>{item}</Text>
                </Pressable>
              );
            })}
            {/* National button for COE/Super User — hidden for RMA tickets */}
            {!isRepairReplaceReturn && (userRole === "COE" || userRole === "Super User") && (
              <Pressable
                key="National"
                onPress={() => {
                  onLevelChange("National");
                  onLevelReferenceChange("");
                  setRegionLimitMessage(null);
                  onFieldBlur?.("level");
                }}
                style={(state: any) => [
                  styles.chip,
                  draft.level === "National" ? styles.chipActive : null,
                  dk && draft.level !== "National" ? dkChip : null,
                  dk && draft.level === "National" ? dkChipActive : null,
                  state.pressed ? styles.chipPressed : null,
                ]}
              >
                <Text
                  style={[
                    styles.chipText,
                    draft.level === "National" ? styles.chipTextActive : null,
                    dk && draft.level !== "National" ? dkChipText : null,
                    dk && draft.level === "National" ? dkChipTextActive : null,
                  ]}
                >
                  National
                </Text>
              </Pressable>
            )}
          </View>
          {fieldErrors.level ? <Text style={[styles.helperText, { color: "#c12c01" }]}>{fieldErrors.level}</Text> : null}
          
          {/* National Issue Indicator - shown when National level is selected */}
          {draft.level === "National" && (
            <Text style={{ marginTop: 8, fontWeight: "600", color: "#2e7d32", fontSize: 14 }}>
              National Issue, all Location Impacted
            </Text>
          )}
          
          {/* Region limit suggestion - shown when trying to select 4th region */}
          {draft.level === "Region" && regionLimitMessage && (
            <Text style={{ marginTop: 8, fontWeight: "500", color: "#2e7d32", fontSize: 14 }}>
              {regionLimitMessage}
            </Text>
          )}
        </View>

        {(() => {
          // Check if National level is selected
          const isNationalSelected = draft.level === "National";

          return isNationalSelected ? (
            <View style={styles.fieldGroup}>
              <Text style={[styles.label, dkLabel]}>Ticket Level Value</Text>
              <TextInput
                style={[styles.input, styles.inputDisabled, dkInputDisabled]}
                value="National"
                editable={false}
              />
            </View>
          ) : (
            <>
              <SelectField
                id="levelReference"
                label={isRepairReplaceReturn ? "Select Location" : `Select ${draft.level}(s)`}
                value={draft.levelReference}
                options={
                  draft.level === "Location"
                    ? locationsWithSapId
                    : draft.level === "Region"
                    ? regionOptions
                    : marketOptions
                }
                required
                placeholder={
                  isRepairReplaceReturn
                    ? "Select a location..."
                    : draft.level === "Location"
                    ? "Select one or more locations..."
                    : draft.level === "Region"
                    ? "Select one or more regions..."
                    : "Select one or more markets..."
                }
                disabled={
                  (draft.level === "Location" && !locationsWithSapId.length) ||
                  (draft.level === "Region" && !regionOptions.length) ||
                  (draft.level === "Market" && !marketOptions.length)
                }
                errorText={fieldErrors.levelReference}
                searchable
                multiSelect={!isRepairReplaceReturn}
                searchPlaceholder={
                  draft.level === "Location"
                    ? "Search by name, location ID, or SAP ID..."
                    : `Search ${draft.level.toLowerCase()}...`
                }
                optionSearchKeys={
                  draft.level === "Location" && locationSearchMetadata
                    ? Object.fromEntries(
                        Object.entries(locationSearchMetadata).map(([name, meta]) => [
                          name,
                          [meta.locationId, meta.sapId].filter(Boolean),
                        ])
                      )
                    : undefined
                }
                optionLabelMap={draft.level === "Location" ? locationOptionLabels : undefined}
                activeDropdownId={mobile ? activeMobileDropdownId : undefined}
                onDropdownToggle={mobile ? setActiveMobileDropdownId : undefined}
                onBlur={() => onFieldBlur?.("levelReference")}
                onChange={!isRepairReplaceReturn && draft.level === "Region" ? handleRegionSelection : onLevelReferenceChange}
              />

              <View style={styles.fieldGroup}>
                <Text style={[styles.label, dkLabel]}>SAP ID</Text>
                <TextInput style={[styles.input, styles.inputDisabled, dkInputDisabled]} value={draft.sapId} editable={false} />
                <Text style={[styles.helperText, dkHelper]}>
                  {draft.level === "Location"
                    ? "Auto-populated from selected Location"
                    : "Auto-populated only when Ticket Level is Location"}
                </Text>
              </View>
            </>
          );
        })()}

        {!hideUrgencyField ? (
          <View style={styles.fieldFull}>
            <Text style={[styles.label, dkLabel]}>Urgency Level <Text style={styles.required}>*</Text></Text>
            <View style={styles.chipRow}>
              {(["Critical", "High", "Medium", "Low"] as const).map((item) => {
                const active = draft.urgency === item;
                return (
                  <Pressable
                    key={item}
                    onPress={() => onUrgencyChange(item)}
                    style={[
                      styles.chip,
                      styles.urgencyChip,
                      active ? styles.chipActive : null,
                      dk && !active ? dkChip : null,
                      dk && active ? dkChipActive : null,
                      { opacity: active ? 1 : 0.88 },
                    ]}
                  >
                    <View style={[styles.urgencyDot, urgencyDotStyleMap[item]]} />
                    <Text style={[styles.chipText, active ? styles.chipTextActive : null, dk && !active ? dkChipText : null, dk && active ? dkChipTextActive : null]}>{item}</Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        ) : null}

        <View style={styles.fieldFull}>
          {isRepairReplaceReturn ? (
            <Text style={[styles.label, dkLabel]}>Notes <Text style={styles.optional}>optional</Text></Text>
          ) : (
            <Text style={[styles.label, dkLabel]}>Issue Description <Text style={styles.required}>*</Text></Text>
          )}
          <TextInput
            style={[
              styles.input,
              styles.inputFull,
              styles.textarea,
              dkTextarea,
              // On web: use minHeight so the browser can grow the textarea naturally.
              // On native: use the dynamic height driven by onContentSizeChange.
              isWeb ? { minHeight: 120 } : { height: descriptionHeight },
            ]}
            value={draft.description}
            onChangeText={onDescriptionChange}
            onContentSizeChange={handleDescriptionContentSizeChange}
            onBlur={() => onFieldBlur?.('description')} // GAP-02
            maxLength={2000} // GAP-04
            multiline
            placeholder={
              isRepairReplaceReturn
                ? "Shipping notes or anything the receiving vendor should know. Optional."
                : "Describe what you were trying to do, what happened, and any error messages you saw. Include steps to reproduce if possible."
            }
            placeholderTextColor={placeholderColor}
          />
          <View style={styles.fieldMetaRow}>
            <Text style={[styles.helperText, dkHelper]}>
              {mobile
                ? "Include steps to reproduce if applicable."
                : "The more detail you provide, the faster we can resolve your issue."}
            </Text>
            <Text style={[styles.helperText, dkHelper, draft.description.length >= 2000 ? { color: "#c12c01", fontWeight: "600" } : null]}>{draft.description.length}/2000</Text>
          </View>
          {fieldErrors.description ? <Text style={[styles.helperText, { color: "#c12c01" }]}>{fieldErrors.description}</Text> : null}
        </View>

        <View style={styles.fieldFull}>
          <Text style={[styles.label, dkLabel]}>Secondary Stakeholders <Text style={styles.optional}>optional</Text></Text>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <TextInput
              style={[styles.input, styles.inputDisabled, dkInputDisabled, { flex: 1 }]}
              value=""
              editable={false}
              placeholder="Use Select Users to add stakeholders"
              placeholderTextColor={placeholderColor}
            />
            <Pressable
              onPress={() => hasStakeholderScope && setShowStakeholderPicker(true)}
              disabled={!hasStakeholderScope}
              style={({ pressed }: { pressed: boolean }) => [
                {
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 6,
                  borderWidth: 1,
                  borderColor: dk ? colors.brand.primary : "#006fce",
                  borderRadius: 8,
                  paddingHorizontal: 14,
                  paddingVertical: 11,
                  backgroundColor: dk ? colors.background.elevated : "transparent",
                  opacity: hasStakeholderScope ? 1 : 0.5,
                },
                pressed && hasStakeholderScope ? { opacity: 0.8 } : null,
              ]}
            >
              <MaterialIcons name="people-outline" size={16} color={dk ? colors.brand.primary : "#006fce"} />
              <Text style={{ fontSize: 13, fontWeight: "700", color: dk ? colors.brand.primary : "#006fce" }}>Select Users</Text>
            </Pressable>
          </View>
          <SelectStakeholdersModal
            visible={showStakeholderPicker}
            onClose={() => setShowStakeholderPicker(false)}
            regions={stakeholderScope.regions}
            markets={stakeholderScope.markets}
            locations={stakeholderScope.locations}
            existingEmails={getStakeholderEmails()}
            onAttach={handleAttachStakeholders}
          />
          {getStakeholderEmails().length > 0 && (
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
              {getStakeholderEmails().map((email) => (
                <View
                  key={email}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    backgroundColor: dk ? colors.background.surface : "#e8f0fe",
                    borderRadius: 16,
                    paddingVertical: 4,
                    paddingLeft: 10,
                    paddingRight: 6,
                    borderWidth: 1,
                    borderColor: dk ? colors.brand.primary : "#006fce",
                  }}
                >
                  <Text style={{ fontSize: 13, color: dk ? colors.brand.primary : "#006fce", marginRight: 4 }}>{email}</Text>
                  <Pressable
                    onPress={() => removeStakeholderEmail(email)}
                    style={({ pressed }: { pressed: boolean }) => ([
                      {
                        width: 18,
                        height: 18,
                        borderRadius: 9,
                        backgroundColor: pressed ? (dk ? colors.brand.primary : "#006fce") : (dk ? colors.background.surface : "#c5d9f5"),
                        justifyContent: "center" as const,
                        alignItems: "center" as const,
                      },
                    ])}
                  >
                    <Text style={{ fontSize: 12, color: dk ? colors.brand.primary : "#006fce", lineHeight: 18, textAlign: "center" }}>×</Text>
                  </Pressable>
                </View>
              ))}
            </View>
          )}
          <Text style={[styles.helperText, dkHelper]}>Stakeholders can view the ticket and receive status updates.</Text>
          <Text style={[styles.helperText, dkHelper]}>Add up to 10 stakeholders using Select Users once a Ticket Level location is chosen.</Text>
          {!hasStakeholderScope ? (
            <Text style={[styles.helperText, dkHelper]}>Select a Ticket Level location to enable Select Users.</Text>
          ) : null}
          {stakeholderError || fieldErrors.stakeholders ? (
            <Text style={[styles.helperText, { color: "#c12c01" }]}>{stakeholderError || fieldErrors.stakeholders}</Text>
          ) : null}
        </View>

        <View style={styles.fieldFull}>
          <Text style={[styles.label, dkLabel]}>Attachments <Text style={styles.optional}>optional</Text></Text>
          <Pressable
            style={(state: any) => [
              styles.uploadBox,
              dk ? { backgroundColor: colors.background.elevated, borderColor: colors.border.default } : null,
              isSubmitting ? { opacity: 0.7 } : null,
              state.pressed ? styles.uploadBoxPressed : null,
            ]}
            onPress={onAddAttachment}
            disabled={isSubmitDisabled || isSubmitting}
          >
            <Text style={[styles.uploadText, dk ? { color: colors.brand.primary } : null]}>Click to upload files</Text>
          </Pressable>

          {isSubmitDisabled && !isSubmitting ? (
            <Text style={[styles.helperText, { color: "#cc6e16", marginTop: 8, fontWeight: "500" }]}>
              Complete all required fields above to enable file uploads.
            </Text>
          ) : null}

          {draft.attachments.map((attachment) => (
            <View key={attachment.id} style={[styles.attachmentChip, dk ? { backgroundColor: colors.background.elevated, borderColor: colors.border.default } : null]}>
              <View style={[styles.attachmentIconWrap, dk ? { backgroundColor: colors.background.surface } : null]}>
                <Text style={styles.attachmentIconText}>IMG</Text>
              </View>
              <View style={styles.attachmentTextWrap}>
                <Text style={[styles.attachmentName, dk ? { color: colors.text.primary } : null]}>{attachment.fileName}</Text>
                <Text style={[styles.attachmentSize, dkHelper]}>{attachment.fileSize}</Text>
              </View>
              <Pressable
                style={(state: any) => [
                  styles.removeAttachmentButton,
                  state.pressed ? styles.removeAttachmentButtonPressed : null,
                ]}
                onPress={() => onRemoveAttachment(String(attachment.id))}
                disabled={isSubmitting}
              >
                <Text style={[styles.removeAttachmentText, dk ? { color: colors.text.secondary } : null]}>×</Text>
              </Pressable>
            </View>
          ))}

          <Text style={[styles.helperText, dkHelper]}>Supported: .jpg, .jpeg, .png, .pdf, .xlsx, .docx. Max 10MB per file, 5 files total.</Text>
          {fieldErrors.attachments ? <Text style={[styles.helperText, { color: "#c12c01" }]}>{fieldErrors.attachments}</Text> : null}
        </View>
          </>
        ) : null}
      </View>

      {!mobile ? (
        <View style={styles.footerActions}>
          <Pressable
            style={(state: any) => [
              styles.btnGhost,
              isSubmitting ? { opacity: 0.7 } : null,
              state.pressed ? styles.btnGhostPressed : null,
            ]}
            onPress={onCancel}
            disabled={isSubmitting}
          >
            <Text style={styles.btnGhostText}>Cancel</Text>
          </Pressable>

          <Pressable
            style={(state: any) => [
              styles.btnPrimary,
              isSubmitActionDisabled ? styles.btnPrimaryDisabled : null,
              state.pressed && !isSubmitActionDisabled ? styles.btnPrimaryPressed : null,
              // Pale-blue disabled fill (#C8D8EE) is not swept and leaves grey text on light blue.
              isDark && isSubmitActionDisabled ? { backgroundColor: "#3A3A3A" } : null,
            ]}
            onPress={onSubmit}
            disabled={isSubmitActionDisabled}
          >
            <Text style={[styles.btnPrimaryText, isSubmitActionDisabled ? styles.btnPrimaryTextDisabled : null]}>
              {isSubmitting ? "Submitting..." : isAccessRequest ? "Submit Request" : "Submit Ticket"}
            </Text>
          </Pressable>
        </View>
      ) : null}
    </View>
  );
});

SubmitTicketFormPanel.displayName = "SubmitTicketFormPanel";

export default SubmitTicketFormPanel;

// ── Access Request card / search styles ───────────────────────────────────────

const arStyle = StyleSheet.create({
  searchBlock: {
    marginTop: 8,
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#d0d0d0",
    borderRadius: 8,
    overflow: "hidden",
  },
  noMatchBox: {
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  noMatchText: {
    fontSize: 13,
    color: "#6b7280",
  },
  searchResultRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
    gap: 10,
    ...(Platform.OS === "web" ? { cursor: "pointer" } as any : {}),
  },
  searchResultRowLast: {
    borderBottomWidth: 0,
  },
  resultName: {
    fontSize: 13,
    fontWeight: "600",
    color: "#2d2d2d",
  },
  resultMeta: {
    fontSize: 11,
    color: "#6b7280",
    marginTop: 1,
  },
  // Selected user card
  userCard: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#f0f4fa",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#d0dff0",
    paddingHorizontal: 12,
    paddingVertical: 9,
    marginTop: 10,
    gap: 10,
    alignSelf: "flex-start",
    maxWidth: "100%",
  },
  avatarCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: "#005cbc",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  avatarText: {
    fontSize: 13,
    fontWeight: "700",
    color: "#ffffff",
  },
  cardName: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "700",
    color: "#1a1a1a",
  },
  cardMeta: {
    fontSize: 11,
    color: "#585858",
    marginTop: 1,
  },
  cardClose: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: "#e5e7eb",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginLeft: 4,
    ...(Platform.OS === "web" ? { cursor: "pointer" } as any : {}),
  },
  cardCloseText: {
    fontSize: 16,
    color: "#374151",
    lineHeight: 20,
    textAlign: "center",
  },
  // ── Temporary Access warning banner ──────────────────────────────────────────
  tempAccessBanner: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    backgroundColor: "#fffbeb",
    borderWidth: 1,
    borderColor: "#fde68a",
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginBottom: 16,
    width: "100%",
  },
  tempAccessBannerText: {
    flex: 1,
    fontSize: 13,
    color: "#78350f",
    lineHeight: 19,
  },
  // ── Accessible Modules Card ────────────────────────────────────────────────
  modulesCard: {
    backgroundColor: "#eff6ff",
    borderWidth: 1,
    borderColor: "#bfdbfe",
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginTop: 16,
    width: "100%",
  },
  modulesCardHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 10,
  },
  modulesCardTitle: {
    fontSize: 13,
    fontWeight: "700",
    color: "#1e3a8a",
  },
  modulesCardSubtitle: {
    fontSize: 11,
    color: "#3b82f6",
    marginTop: 2,
  },
  modulesSameAccessBadge: {
    alignSelf: "flex-start",
    backgroundColor: "#dcfce7",
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 3,
    marginBottom: 10,
  },
  modulesSameAccessBadgeText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#15803d",
    letterSpacing: 0.4,
  },
  modulesAccessLabel: {
    fontSize: 12,
    color: "#374151",
    marginBottom: 8,
  },
  modulesPillsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  modulePill: {
    borderWidth: 1,
    borderColor: "#93c5fd",
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 5,
    backgroundColor: "#ffffff",
    maxWidth: "100%",
  },
  modulePillMuted: {
    borderColor: "#d1d5db",
    backgroundColor: "#f8fafc",
  },
  modulePillLoading: {
    paddingHorizontal: 0,
    paddingVertical: 0,
    backgroundColor: "#eff6ff",
    shadowColor: "#3b82f6",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.18,
    shadowRadius: 4,
    elevation: 2,
  },
  modulePillSkeleton: {
    width: 92,
    height: 18,
    borderRadius: 999,
    backgroundColor: "#dbeafe",
  },
  modulePillText: {
    fontSize: 13,
    fontWeight: "600",
    color: "#1e40af",
    flexShrink: 1,
    flexWrap: "wrap",
  },
  modulePillStatus: {
    fontSize: 11,
    color: "#6b7280",
    marginTop: 2,
    fontWeight: "500",
  },
  // ── Full-access vs view-only module rows ("What does this access give me?") ──
  viewOnlyLabel: {
    marginTop: 12,
  },
  moduleAccessItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#93c5fd",
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 6,
    maxWidth: "100%",
  },
  moduleAccessItemText: {
    fontSize: 13,
    color: "#1e40af",
    fontWeight: "600",
    flexShrink: 1,
  },
  modulesEndDateNote: {
    marginTop: 10,
    fontSize: 12,
    color: "#374151",
    lineHeight: 18,
    borderTopWidth: 1,
    borderTopColor: "#bfdbfe",
    paddingTop: 8,
  },
});
